import {
  BASE_URL,
  SECONDARY_KEYWORD_BY_SERVICE,
  SEO_SERVICE_BAR_BAT,
  SEO_SERVICE_EVENT,
  SEO_SERVICE_HENNA,
  SEO_SERVICE_WEDDING,
  withBet,
  type FAQItem,
  type PlanningItem,
  type SeoService,
} from '@/constants/core';
import {
  EVENT_TYPES,
  SEO_ALL_CITIES,
  findRegionByCity,
  globalTypePath,
  typeLocationPath,
  type EventType,
  type Region,
  type SeoCity,
} from '@/constants/geo';
import type { I18nConstants } from '@/i18n/locales/i18n.factory';

export type PlanningArticleKey =
  | 'wedding_chuppah_song'
  | 'wedding_timeline'
  | 'wedding_attractions'
  | 'wedding_musicians'
  | 'wedding_slow_song'
  | 'wedding_music'
  | 'henna_entrance'
  | 'henna_timeline'
  | 'henna_attractions'
  | 'henna_musicians'
  | 'henna_music'
  | 'henna_moroccan'
  | 'henna_kurdish'
  | 'henna_tunisian'
  | 'henna_yemenite'
  | 'henna_indian'
  | 'henna_iraqi'
  | 'henna_persian'
  | 'bar_bat_recording'
  | 'bar_bat_entrance'
  | 'bar_bat_timeline'
  | 'bar_bat_attractions'
  | 'bar_bat_musicians'
  | 'bar_bat_music'
  | 'event_entrance'
  | 'event_music'
  | 'event_music_timeline'
  | 'event_attractions'
  | 'event_musicians';

export type PlanningArticle = {
  key: PlanningArticleKey | string;
  slug: string;
  service: SeoService;
  title: string;
  seoTitle?: string;
  sectionTitle: string;
  description: string;
  seoDescription?: string;
  carouselDescription?: string;
  image: string;
  imageSrcSet?: string;
  imageSizes?: string;
  imageAlt: string;
  intro: string;
  bullets: string[];
  cta: string;
  parentEventType: EventType;
  parentHref?: string;
  locationCity?: Exclude<SeoCity, ''>;
  serviceDisplayLabel: string;
  parentServiceLabel: string;
  parentCtaLabel: string;
  parentLinkLabel: string;
  trustTitle: string;
  trustParagraphs: string[];
  workTitle: string;
  workBullets: string[];
  faqs: FAQItem[];
  relatedArticles: PlanningItem[];
  articleSection: string;
  keywords: string[];
  wordCount: number;
};

type PlanningArticleMeta = {
  key: PlanningArticleKey;
  slug: string;
  service: SeoService;
  image: string;
};

export const PLANNING_GUIDE_PATH = '/מדריך-אירועים/';
const GUIDE_BASE_PATH = PLANNING_GUIDE_PATH.replace(/\/$/, '');

const WEDDING_BLOG_IMAGE_BASE =
  'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/Blog/%D7%97%D7%AA%D7%95%D7%A0%D7%94';

const WEDDING_BLOG_IMAGES: Record<
  Extract<
    PlanningArticleKey,
    | 'wedding_chuppah_song'
    | 'wedding_timeline'
    | 'wedding_attractions'
    | 'wedding_musicians'
    | 'wedding_slow_song'
    | 'wedding_music'
  >,
  string
> = {
  wedding_chuppah_song: `${WEDDING_BLOG_IMAGE_BASE}/%D7%9B%D7%A0%D7%99%D7%A1%D7%94+%D7%9C%D7%97%D7%95%D7%A4%D7%94.webp`,
  wedding_timeline: `${WEDDING_BLOG_IMAGE_BASE}/%D7%AA%D7%9B%D7%A0%D7%95%D7%9F+%D7%9C%D7%95%D7%96+%D7%9C%D7%97%D7%AA%D7%95%D7%A0%D7%94.webp`,
  wedding_attractions: `${WEDDING_BLOG_IMAGE_BASE}/%D7%90%D7%98%D7%A8%D7%A7%D7%A6%D7%99%D7%95%D7%AA+%D7%9C%D7%97%D7%AA%D7%95%D7%A0%D7%94.webp`,
  wedding_musicians: `${WEDDING_BLOG_IMAGE_BASE}/%D7%A0%D7%92%D7%A0%D7%99%D7%9D+%D7%9C%D7%97%D7%AA%D7%95%D7%A0%D7%94.webp`,
  wedding_slow_song: `${WEDDING_BLOG_IMAGE_BASE}/%D7%91%D7%97%D7%99%D7%A8%D7%AA+%D7%A9%D7%99%D7%A8+%D7%A1%D7%9C%D7%95%D7%90%D7%95.webp`,
  wedding_music: `${WEDDING_BLOG_IMAGE_BASE}/%D7%91%D7%97%D7%99%D7%A8%D7%AA+%D7%9E%D7%95%D7%96%D7%99%D7%A7%D7%94.webp`,
};

const HENNA_BLOG_IMAGE_BASE =
  'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/Blog/%D7%97%D7%99%D7%A0%D7%94';

const HENNA_BLOG_IMAGES: Record<
  Extract<
    PlanningArticleKey,
    | 'henna_entrance'
    | 'henna_timeline'
    | 'henna_attractions'
    | 'henna_musicians'
    | 'henna_music'
    | 'henna_moroccan'
    | 'henna_kurdish'
    | 'henna_tunisian'
    | 'henna_yemenite'
    | 'henna_indian'
    | 'henna_iraqi'
    | 'henna_persian'
  >,
  string
> = {
  henna_entrance: `${HENNA_BLOG_IMAGE_BASE}/%D7%9B%D7%A0%D7%99%D7%A1%D7%94+%D7%9C%D7%97%D7%99%D7%A0%D7%94.webp`,
  henna_timeline: `${HENNA_BLOG_IMAGE_BASE}/%D7%AA%D7%9B%D7%A0%D7%95%D7%9F+%D7%9C%D7%95%D7%96+%D7%9C%D7%97%D7%99%D7%A0%D7%94.webp`,
  henna_attractions: `${HENNA_BLOG_IMAGE_BASE}/%D7%90%D7%98%D7%A8%D7%A7%D7%A6%D7%99%D7%95%D7%AA+%D7%9C%D7%97%D7%99%D7%A0%D7%94.webp`,
  henna_musicians: `${HENNA_BLOG_IMAGE_BASE}/%D7%A0%D7%92%D7%A0%D7%99%D7%9D+%D7%9C%D7%97%D7%99%D7%A0%D7%94.webp`,
  henna_music: `${HENNA_BLOG_IMAGE_BASE}/%D7%91%D7%97%D7%99%D7%A8%D7%AA+%D7%9E%D7%95%D7%96%D7%99%D7%A7%D7%94+%D7%9C%D7%97%D7%99%D7%A0%D7%94.webp`,
  henna_moroccan: `${HENNA_BLOG_IMAGE_BASE}/%D7%97%D7%99%D7%A0%D7%94+%D7%9E%D7%A8%D7%95%D7%A7%D7%90%D7%99%D7%AA.webp`,
  henna_kurdish: `${HENNA_BLOG_IMAGE_BASE}/%D7%97%D7%99%D7%A0%D7%94+%D7%9B%D7%95%D7%A8%D7%93%D7%99%D7%AA.webp`,
  henna_tunisian: `${HENNA_BLOG_IMAGE_BASE}/%D7%97%D7%99%D7%A0%D7%94+%D7%AA%D7%95%D7%A0%D7%99%D7%A1%D7%90%D7%99%D7%AA.webp`,
  henna_yemenite: `${HENNA_BLOG_IMAGE_BASE}/%D7%97%D7%99%D7%A0%D7%94+%D7%AA%D7%99%D7%9E%D7%A0%D7%99%D7%AA.webp`,
  henna_indian: `${HENNA_BLOG_IMAGE_BASE}/%D7%97%D7%99%D7%A0%D7%94+%D7%94%D7%95%D7%93%D7%99%D7%AA.webp`,
  henna_iraqi: `${HENNA_BLOG_IMAGE_BASE}/%D7%97%D7%99%D7%A0%D7%94+%D7%A2%D7%99%D7%A8%D7%90%D7%A7%D7%99%D7%AA.webp`,
  henna_persian: `${HENNA_BLOG_IMAGE_BASE}/%D7%97%D7%99%D7%A0%D7%94+%D7%A4%D7%A8%D7%A1%D7%99%D7%AA.webp`,
};

const EVENT_BLOG_IMAGE_BASE =
  'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/Blog/%D7%90%D7%99%D7%A8%D7%95%D7%A2';

const EVENT_BLOG_IMAGES: Record<
  Extract<
    PlanningArticleKey,
    | 'event_music'
    | 'event_music_timeline'
    | 'event_entrance'
    | 'event_attractions'
    | 'event_musicians'
  >,
  string
> = {
  event_music: `${EVENT_BLOG_IMAGE_BASE}/%D7%91%D7%97%D7%99%D7%A8%D7%AA+%D7%9E%D7%95%D7%96%D7%99%D7%A7%D7%94+%D7%9C%D7%90%D7%99%D7%A8%D7%95%D7%A2.webp`,
  event_music_timeline: `${EVENT_BLOG_IMAGE_BASE}/%D7%AA%D7%9B%D7%A0%D7%95%D7%9F+%D7%9C%D7%95%D7%B4%D7%96+%D7%9C%D7%90%D7%99%D7%A8%D7%95%D7%A2.webp`,
  event_entrance: `${EVENT_BLOG_IMAGE_BASE}/%D7%9B%D7%A0%D7%99%D7%A1%D7%94+%D7%9C%D7%90%D7%99%D7%A8%D7%95%D7%A2.webp`,
  event_attractions: `${EVENT_BLOG_IMAGE_BASE}/%D7%90%D7%98%D7%A8%D7%A7%D7%A6%D7%99%D7%95%D7%AA+%D7%9C%D7%90%D7%99%D7%A8%D7%95%D7%A2.webp`,
  event_musicians: `${EVENT_BLOG_IMAGE_BASE}/%D7%A0%D7%92%D7%A0%D7%99%D7%9D+%D7%9C%D7%90%D7%99%D7%A8%D7%95%D7%A2.webp`,
};

const BAR_BAT_BLOG_IMAGE_BASE =
  'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/Blog/%D7%91%D7%A8-%D7%9E%D7%A6%D7%95%D7%95%D7%94';

const BAR_BAT_BLOG_IMAGES: Record<
  Extract<
    PlanningArticleKey,
    | 'bar_bat_recording'
    | 'bar_bat_entrance'
    | 'bar_bat_timeline'
    | 'bar_bat_attractions'
    | 'bar_bat_musicians'
    | 'bar_bat_music'
  >,
  string
> = {
  bar_bat_recording: `${BAR_BAT_BLOG_IMAGE_BASE}/%D7%94%D7%A7%D7%9C%D7%98%D7%95%D7%AA+%D7%9C%D7%91%D7%A8%3A%D7%91%D7%AA+%D7%9E%D7%A6%D7%95%D7%95%D7%94.webp`,
  bar_bat_entrance: `${BAR_BAT_BLOG_IMAGE_BASE}/%D7%9B%D7%A0%D7%99%D7%A1%D7%94+%D7%9C%D7%91%D7%A8%3A%D7%91%D7%AA+%D7%9E%D7%A6%D7%95%D7%95%D7%94.webp`,
  bar_bat_timeline: `${BAR_BAT_BLOG_IMAGE_BASE}/%D7%AA%D7%9B%D7%A0%D7%95%D7%9F+%D7%9C%D7%95%D7%B4%D7%96+%D7%9C%D7%91%D7%A8%3A%D7%91%D7%AA+%D7%9E%D7%A6%D7%95%D7%95%D7%94.webp`,
  bar_bat_attractions: `${BAR_BAT_BLOG_IMAGE_BASE}/%D7%90%D7%98%D7%A8%D7%A7%D7%A6%D7%99%D7%95%D7%AA+%D7%9C%D7%91%D7%A8%3A%D7%91%D7%AA+%D7%9E%D7%A6%D7%95%D7%95%D7%94.webp`,
  bar_bat_musicians: `${BAR_BAT_BLOG_IMAGE_BASE}/%D7%A0%D7%92%D7%A0%D7%99%D7%9D+%D7%9C%D7%91%D7%A8%3A%D7%91%D7%AA+%D7%9E%D7%A6%D7%95%D7%95%D7%94.webp`,
  bar_bat_music: `${BAR_BAT_BLOG_IMAGE_BASE}/%D7%91%D7%97%D7%99%D7%A8%D7%AA+%D7%A9%D7%99%D7%A8%D7%99%D7%9D+%D7%9C%D7%91%D7%A8%3A%D7%91%D7%AA+%D7%9E%D7%A6%D7%95%D7%95%D7%94.webp`,
};

const articlePath = (slug: string) => `${GUIDE_BASE_PATH}/${slug}/`;
const planningImageVariant = (key: string, width: 560 | 640 | 960) =>
  `/images/planning/${key}-${width}.webp`;
const planningImageSrcSet = (key: string) =>
  `${planningImageVariant(key, 560)} 560w, ${planningImageVariant(key, 640)} 640w, ${planningImageVariant(key, 960)} 960w`;
const CITY_VENUE_GUIDE_IMAGE_KEY = 'city_venue_guide_20260724';
const CITY_VENUE_GUIDE_IMAGE = `${BASE_URL}${planningImageVariant(CITY_VENUE_GUIDE_IMAGE_KEY, 960)}`;
export const PLANNING_CARD_IMAGE_SIZES =
  '(max-width: 640px) calc(100vw - 92px), (max-width: 768px) calc(100vw - 120px), 360px';
export const PLANNING_ARTICLE_IMAGE_SIZES =
  '(max-width: 768px) calc(100vw - 36px), (max-width: 1200px) 42vw, 520px';
export const CITY_VENUE_GUIDE_MIN_VENUES = 6;

function trimPlanningCardDescription(text: string) {
  const cleanText = text.replace(/\s+/g, ' ').trim();
  const maxLength = 320;

  if (cleanText.length <= maxLength) return cleanText;

  const shortened = cleanText.slice(0, maxLength + 1);
  const lastSpaceIndex = shortened.lastIndexOf(' ');

  return `${shortened.slice(0, lastSpaceIndex > 220 ? lastSpaceIndex : maxLength).trim()}...`;
}

function trimPlanningSeoText(text: string, maxLength = 340) {
  const cleanText = String(text ?? '')
    .replace(/\s+/g, ' ')
    .trim();
  if (cleanText.length <= maxLength) return cleanText;

  const shortened = cleanText.slice(0, maxLength + 1);
  const lastSpaceIndex = shortened.lastIndexOf(' ');
  return `${shortened.slice(0, lastSpaceIndex > 180 ? lastSpaceIndex : maxLength).trim()}...`;
}

function buildPlanningCardFallbackDescription(article: PlanningArticle) {
  return [
    article.description,
    article.intro,
    ...(article.bullets ?? []).slice(0, 3),
    article.cta,
  ]
    .filter(Boolean)
    .join(' ');
}

function buildPlanningCardDescription(
  article: PlanningArticle,
  overrideDescription?: string,
) {
  return trimPlanningCardDescription(
    overrideDescription ??
      article.carouselDescription ??
      buildPlanningCardFallbackDescription(article) ??
      article.description,
  );
}

function buildPlanningSeoTitle(
  key: string,
  title: string,
  service: SeoService,
  lang = 'he',
) {
  if (lang === 'he') return title;

  if (key.startsWith('city_venue_guide_')) {
    if (lang === 'en') {
      return `${title} for atmosphere, sound and dance-floor flow`;
    }

    if (lang === 'fr') {
      return `${title} avec ambiance, son et piste`;
    }

    if (lang === 'ru') {
      return `${title} с атмосферой, звуком и танцполом`;
    }
  }

  if (service === SEO_SERVICE_WEDDING) {
    if (lang === 'en') return `${title} | Wedding DJ guide`;
    if (lang === 'fr') return `${title} | Guide DJ mariage`;
    if (lang === 'ru') return `${title} | Гид DJ на свадьбу`;
  }

  if (service === SEO_SERVICE_HENNA) {
    if (lang === 'en') return `${title} | Henna DJ guide`;
    if (lang === 'fr') return `${title} | Guide DJ henné`;
    if (lang === 'ru') return `${title} | Гид DJ на хину`;
  }

  if (service === SEO_SERVICE_BAR_BAT) {
    if (lang === 'en') return `${title} | Bar/Bat Mitzvah DJ guide`;
    if (lang === 'fr') return `${title} | Guide DJ bar/bat mitzvah`;
    if (lang === 'ru') return `${title} | Гид DJ для бар/бат-мицвы`;
  }

  if (lang === 'en') return `${title} | Event DJ guide`;
  if (lang === 'fr') return `${title} | Guide DJ événement`;
  if (lang === 'ru') return `${title} | Гид DJ на событие`;
  return title;
}

const PLANNING_SEO_DESCRIPTION_BY_KEY: Partial<
  Record<PlanningArticleKey, string>
> = {
  wedding_chuppah_song:
    'בחירת שיר כניסה לחופה לפי קצב הליכה, סדר מלווים, כניסת חתן וכלה, ווליום, נגנים, עשן או זיקוקים ברגע הנכון.',
  wedding_timeline:
    'תכנון לו״ז חתונה עם קבלת פנים, חופה, כניסות לרחבת הריקודים, סלואו, הפסקות אוכל ואפטר פארטי בתיאום עם הדיג׳יי והספקים.',
  wedding_attractions:
    'אטרקציות לחתונה כמו זיקוקים, עשן כבד, בלונים מתפוצצים, לייזרים, אותיות מעוצבות, עמדות צילום, מגנטים, קיאק פירות, נגנים, מתנפחים ועוד.',
  wedding_musicians:
    'נגנים לחתונה כמו כנר, סקסופון, גיטרה, בוזוקי, דרבוקות, מתופפים, זמרים או להקה עם תיאום סאונד ודיג׳יי.',
  wedding_slow_song:
    'בחירת שיר סלואו לחתונה שמתאים לריקוד, עם רגע שיא לזיקוקים, קונפטי, עשן כבד, בלונים מתפוצצים ועיצוב מיוחד.',
  wedding_music:
    'בחירת מוזיקה לחתונה לפי קהל ורחבה עם מיינסטרים, מזרחית, רגאטון, מוזיקה שחורה, נוסטלגיה, לועזית, טראנס, טכנו, אפרו, 70s-80s-90s ועוד סגנונות.',
  henna_entrance:
    'כניסה לחינה עם שיר כניסה, סדר הארוס והארוסה, הורים, מלווים, תלבושות, מעגלים ותיאום מול הדיג׳יי לפתיחת ערב מסודרת.',
  henna_timeline:
    'תכנון לו״ז חינה עם כניסה, החלפות לבוש, טקס מריחת חינה, רגעי משפחה, מנות וריקודים בתיאום מול האולם והדיג׳יי.',
  henna_attractions:
    'אטרקציות לחינה כמו קיאק פירות, רקדנית בטן, עמדות צילום, מגנטים, בר קוקטיילים, תלבושות, בלונים מתפוצצים, עשן כבד, נגנים, מתנפחים ועוד.',
  henna_musicians:
    'נגנים לחינה כמו דרבוקיסט, כנר, סקסופון, בוזוקי, גיטרה, דרבוקות, מתופפים, קלידים, זמרים או להקה בתיאום עם הדיג׳יי.',
  henna_music:
    'בחירת מוזיקה לחינה עם שירי חינה מרוקאית, תימנית, פרסית, עיראקית, כורדית, תוניסאית והודית לצד מזרחית ולהיטים לרחבה.',
  henna_moroccan:
    'חינה מרוקאית עם שירי כניסה, לבוש מסורתי, טקס מריחת חינה, שירי חינה מרוקאית, מעגלים ומעבר נכון לריקודים.',
  henna_kurdish:
    'חינה כורדית עם שירים כורדיים, כניסות, טקס מריחת חינה, רגעי משפחה, מעגלים ותיאום מוזיקה שמכבד את המסורת.',
  henna_tunisian:
    'חינה תוניסאית עם שירי כניסה, מקצבים תוניסאיים, טקס מריחת חינה, רגעי משפחה ומעבר ברור מהמוזיקה המסורתית לריקודים.',
  henna_yemenite:
    'חינה תימנית עם שירים תימניים, כניסות, תופים, טקס מריחת חינה, משפחה ומעבר מדויק בין המסורת לרחבת הריקודים.',
  henna_indian:
    'חינה הודית עם מוזיקה הודית לחינה, צבעים, כניסות, טקס, שירים משפחתיים ותיאום בין המסורת לריקודים עדכניים.',
  henna_iraqi:
    'חינה עיראקית עם שירים עיראקיים, כניסות, טקס מריחת חינה, רגעי משפחה ומוזיקה שמחברת מסורת, קהל וריקודים.',
  henna_persian:
    'חינה פרסית עם שירים פרסיים לחינה, כניסות, טקס מריחת חינה, משפחה, אלגנטיות ומעבר מתוכנן לריקודים.',
  bar_bat_recording:
    'הקלטת שיר לבר מצווה או בת מצווה עם בחירת פלייבק, כתיבת מילים, חזרות ותיאום הכניסה, הקליפ או ההפתעה עם הדיג׳יי.',
  bar_bat_entrance:
    'כניסה לבר/בת מצווה עם שיר כניסה, הכרזה, קליפ, ריקוד, חברים, משפחה ותיאום מוזיקה, תאורה ומיקרופון לרגע פתיחה ברור.',
  bar_bat_timeline:
    'תכנון לו״ז בר/בת מצווה עם קבלת פנים, כניסת מצווה, ברכות, קליפ, אוכל, אטרקציות ורחבת נוער ומשפחה בתיאום עם הדיג׳יי.',
  bar_bat_attractions:
    'אטרקציות לבר/בת מצווה כמו זיקוקים, עשן כבד, בלונים מתפוצצים, לייזרים, אותיות מעוצבות, עמדות צילום, מגנטים, קיאק פירות, נגנים, מתנפחים ועוד.',
  bar_bat_musicians:
    'נגנים לבר/בת מצווה כמו כנר, סקסופון, דרבוקות, מתופפים, זמרים, קלידים או להקה עם תיאום כניסה, סאונד ורחבה.',
  bar_bat_music:
    'בחירת מוזיקה לבר/בת מצווה לפי נוער ומשפחה עם מיינסטרים, מזרחית, רגאטון, מוזיקה שחורה, נוסטלגיה, לועזית, טראנס, טכנו, אפרו, 70s-80s-90s ועוד סגנונות.',
  event_entrance:
    'כניסה לאירוע עם שיר פתיחה, הכרזה, תאורה, מיקרופון, מסך או אפקטים ותיאום מול הדיג׳יי כדי להתחיל את הערב ברור.',
  event_music:
    'בחירת מוזיקה לאירוע לפי קהל ואווירה עם מיינסטרים, מזרחית, רגאטון, מוזיקה שחורה, נוסטלגיה, לועזית, טראנס, טכנו, אפרו, 70s-80s-90s ועוד סגנונות.',
  event_music_timeline:
    'תכנון לו״ז לאירוע עם קבלת פנים, פתיחה, ברכות, מצגת, קליפ, אוכל וריקודים בתיאום בין האולם, המנחה, הצלמים והדיג׳יי.',
  event_attractions:
    'אטרקציות לאירוע כמו זיקוקים, עשן כבד, לייזרים, עמדות צילום, מגנטים, בלונים מתפוצצים, אותיות מעוצבות, קיאק פירות, נגנים, מתנפחים, אפטר פארטי ועוד.',
  event_musicians:
    'נגנים לאירוע כמו כנר, סקסופון, בוזוקי, גיטרה, דרבוקות, מתופפים, זמרים, קלידים או להקה בתיאום סאונד ומעברים.',
};

function buildPlanningSeoDescription(args: {
  key: string;
  title: string;
  fallback: string;
  lang?: string;
  venueNames?: readonly string[];
}) {
  const { key, title, fallback, lang = 'he', venueNames = [] } = args;

  if (lang !== 'he') {
    const localizedTitle = trimPlanningSeoText(title, 80);

    if (lang === 'en') {
      return trimPlanningSeoText(
        `Practical tips for ${localizedTitle} with key choices, preparation steps and coordination points before the event.`,
        172,
      );
    }

    if (lang === 'fr') {
      return trimPlanningSeoText(
        `Conseils pratiques pour ${localizedTitle} avec les choix, préparatifs et points à coordonner avant l’événement.`,
        172,
      );
    }

    if (lang === 'ru') {
      return trimPlanningSeoText(
        `Практические советы по теме «${localizedTitle}» с выбором, подготовкой и согласованиями перед мероприятием.`,
        172,
      );
    }

    return trimPlanningSeoText(fallback, 172);
  }

  if (key.startsWith('city_venue_guide_')) {
    const cityLabel = title.replace(/^בחירת אולם\s+/, '').trim();
    const venuePreview = venueNames.length
      ? previewVenueNames(venueNames, 3, 'ועוד')
      : '';
    const venuePart = venuePreview ? ` אולמות בעיר כמו ${venuePreview}.` : '';

    return trimPlanningSeoText(
      `בחירת אולם אירועים ${cityLabel} לפי כמות אורחים, תקציב, רחבה, סאונד, קבלת פנים, חופה ואוכל.${venuePart}`,
      172,
    );
  }

  const description =
    PLANNING_SEO_DESCRIPTION_BY_KEY[key as PlanningArticleKey];
  if (description) return trimPlanningSeoText(description, 172);

  return (
    trimPlanningSeoText(
      `${title} עם הכנות חשובות, תיאום מול הדיג׳יי, האולם והספקים ובחירה שמתאימה לקהל, ללוח הזמנים ולאופי האירוע.`,
      172,
    ) || trimPlanningSeoText(fallback, 172)
  );
}

function buildPlanningParentCtaLabel(
  service: SeoService,
  parentServiceLabel: string,
) {
  const hasHebrew = /[\u0590-\u05FF]/.test(parentServiceLabel);
  if (service === SEO_SERVICE_BAR_BAT && hasHebrew) {
    return 'די ג׳יי לבר/בת מצווה';
  }

  return parentServiceLabel;
}

function getPlanningServiceDisplayLabel(
  service: SeoService,
  i18n: I18nConstants,
) {
  if (service === SEO_SERVICE_WEDDING) {
    return String(i18n.WORDS.events.wedding.one);
  }

  if (service === SEO_SERVICE_HENNA) {
    return String(i18n.WORDS.events.henna.one);
  }

  if (service === SEO_SERVICE_BAR_BAT) {
    return String(i18n.WORDS.events.bar_bat.one);
  }

  return String(i18n.WORDS.events.generic.one);
}

export function getPlanningArticleParentHref(
  article: Pick<PlanningArticle, 'parentHref' | 'parentEventType'>,
) {
  return article.parentHref ?? globalTypePath(article.parentEventType);
}

function buildPlanningItemSeoBullets(article: PlanningArticle) {
  const candidates = String(article.key).startsWith('city_venue_guide_')
    ? [
        article.bullets[0],
        article.bullets[1],
        article.bullets[2],
        article.bullets[3],
        article.bullets[article.bullets.length - 1],
        article.workBullets[0],
      ]
    : [
        ...article.bullets.slice(0, 3),
        ...article.workBullets.slice(0, 2),
        article.trustParagraphs[0],
      ];
  const seen = new Set<string>();

  return candidates
    .map(text => trimPlanningSeoText(text, 260))
    .filter(text => {
      if (!text || seen.has(text)) return false;
      seen.add(text);
      return true;
    })
    .slice(0, 6);
}

export const EVENT_PLANNING_ARTICLE_META: PlanningArticleMeta[] = [
  {
    key: 'wedding_chuppah_song',
    slug: 'בחירת-שיר-כניסה-לחופה',
    service: SEO_SERVICE_WEDDING,
    image: WEDDING_BLOG_IMAGES.wedding_chuppah_song,
  },
  {
    key: 'wedding_timeline',
    slug: 'תכנון-לוז-חתונה',
    service: SEO_SERVICE_WEDDING,
    image: WEDDING_BLOG_IMAGES.wedding_timeline,
  },
  {
    key: 'wedding_attractions',
    slug: 'אטרקציות-לחתונה',
    service: SEO_SERVICE_WEDDING,
    image: WEDDING_BLOG_IMAGES.wedding_attractions,
  },
  {
    key: 'wedding_musicians',
    slug: 'נגנים-לחתונה',
    service: SEO_SERVICE_WEDDING,
    image: WEDDING_BLOG_IMAGES.wedding_musicians,
  },
  {
    key: 'wedding_slow_song',
    slug: 'בחירת-שיר-לריקוד-סלואו',
    service: SEO_SERVICE_WEDDING,
    image: WEDDING_BLOG_IMAGES.wedding_slow_song,
  },
  {
    key: 'wedding_music',
    slug: 'בחירת-מוזיקה-לחתונה',
    service: SEO_SERVICE_WEDDING,
    image: WEDDING_BLOG_IMAGES.wedding_music,
  },
  {
    key: 'henna_entrance',
    slug: 'כניסה-לחינה',
    service: SEO_SERVICE_HENNA,
    image: HENNA_BLOG_IMAGES.henna_entrance,
  },
  {
    key: 'henna_timeline',
    slug: 'תכנון-לוז-חינה',
    service: SEO_SERVICE_HENNA,
    image: HENNA_BLOG_IMAGES.henna_timeline,
  },
  {
    key: 'henna_attractions',
    slug: 'אטרקציות-לחינה',
    service: SEO_SERVICE_HENNA,
    image: HENNA_BLOG_IMAGES.henna_attractions,
  },
  {
    key: 'henna_musicians',
    slug: 'נגנים-לחינה',
    service: SEO_SERVICE_HENNA,
    image: HENNA_BLOG_IMAGES.henna_musicians,
  },
  {
    key: 'henna_music',
    slug: 'בחירת-מוזיקה-לחינה',
    service: SEO_SERVICE_HENNA,
    image: HENNA_BLOG_IMAGES.henna_music,
  },
  {
    key: 'event_music',
    slug: 'בחירת-מוזיקה-לאירוע',
    service: SEO_SERVICE_EVENT,
    image: EVENT_BLOG_IMAGES.event_music,
  },
  {
    key: 'event_music_timeline',
    slug: 'תכנון-לוז-לאירוע',
    service: SEO_SERVICE_EVENT,
    image: EVENT_BLOG_IMAGES.event_music_timeline,
  },
  {
    key: 'event_entrance',
    slug: 'כניסה-לאירוע',
    service: SEO_SERVICE_EVENT,
    image: EVENT_BLOG_IMAGES.event_entrance,
  },
  {
    key: 'event_attractions',
    slug: 'אטרקציות-לאירוע',
    service: SEO_SERVICE_EVENT,
    image: EVENT_BLOG_IMAGES.event_attractions,
  },
  {
    key: 'event_musicians',
    slug: 'נגנים-לאירוע',
    service: SEO_SERVICE_EVENT,
    image: EVENT_BLOG_IMAGES.event_musicians,
  },
  {
    key: 'henna_moroccan',
    slug: 'חינה-מרוקאית',
    service: SEO_SERVICE_HENNA,
    image: HENNA_BLOG_IMAGES.henna_moroccan,
  },
  {
    key: 'henna_kurdish',
    slug: 'חינה-כורדית',
    service: SEO_SERVICE_HENNA,
    image: HENNA_BLOG_IMAGES.henna_kurdish,
  },
  {
    key: 'henna_tunisian',
    slug: 'חינה-תוניסאית',
    service: SEO_SERVICE_HENNA,
    image: HENNA_BLOG_IMAGES.henna_tunisian,
  },
  {
    key: 'henna_yemenite',
    slug: 'חינה-תימנית',
    service: SEO_SERVICE_HENNA,
    image: HENNA_BLOG_IMAGES.henna_yemenite,
  },
  {
    key: 'henna_indian',
    slug: 'חינה-הודית',
    service: SEO_SERVICE_HENNA,
    image: HENNA_BLOG_IMAGES.henna_indian,
  },
  {
    key: 'henna_iraqi',
    slug: 'חינה-עיראקית',
    service: SEO_SERVICE_HENNA,
    image: HENNA_BLOG_IMAGES.henna_iraqi,
  },
  {
    key: 'henna_persian',
    slug: 'חינה-פרסית',
    service: SEO_SERVICE_HENNA,
    image: HENNA_BLOG_IMAGES.henna_persian,
  },
  {
    key: 'bar_bat_recording',
    slug: 'הקלטת-שירים-לבר-בת-מצווה',
    service: SEO_SERVICE_BAR_BAT,
    image: BAR_BAT_BLOG_IMAGES.bar_bat_recording,
  },
  {
    key: 'bar_bat_entrance',
    slug: 'כניסה-לבר-בת-מצווה',
    service: SEO_SERVICE_BAR_BAT,
    image: BAR_BAT_BLOG_IMAGES.bar_bat_entrance,
  },
  {
    key: 'bar_bat_timeline',
    slug: 'תכנון-לוז-בר-בת-מצווה',
    service: SEO_SERVICE_BAR_BAT,
    image: BAR_BAT_BLOG_IMAGES.bar_bat_timeline,
  },
  {
    key: 'bar_bat_attractions',
    slug: 'אטרקציות-לבר-בת-מצווה',
    service: SEO_SERVICE_BAR_BAT,
    image: BAR_BAT_BLOG_IMAGES.bar_bat_attractions,
  },
  {
    key: 'bar_bat_musicians',
    slug: 'נגנים-לבר-בת-מצווה',
    service: SEO_SERVICE_BAR_BAT,
    image: BAR_BAT_BLOG_IMAGES.bar_bat_musicians,
  },
  {
    key: 'bar_bat_music',
    slug: 'בחירת-מוזיקה-לבר-בת-מצווה',
    service: SEO_SERVICE_BAR_BAT,
    image: BAR_BAT_BLOG_IMAGES.bar_bat_music,
  },
];

const metaBySlug = new Map(
  EVENT_PLANNING_ARTICLE_META.map(item => [item.slug, item]),
);
const metaByKey = new Map(
  EVENT_PLANNING_ARTICLE_META.map(item => [item.key, item]),
);

const typeByService: Record<SeoService, EventType> = {
  [SEO_SERVICE_WEDDING]: 'חתונה',
  [SEO_SERVICE_HENNA]: 'חינה',
  [SEO_SERVICE_BAR_BAT]: 'בר-מצווה',
  [SEO_SERVICE_EVENT]: 'אירוע',
};

function getSectionKeysForService(service: SeoService, i18n: I18nConstants) {
  const sectionCopy =
    i18n.PLANNING_SECTIONS[service] ??
    i18n.PLANNING_SECTIONS[SEO_SERVICE_EVENT];

  return (sectionCopy.keys as PlanningArticleKey[]).filter(key =>
    metaByKey.has(key),
  );
}

function estimateWordCount(parts: Array<string | string[] | undefined>) {
  return parts
    .flatMap(part => (Array.isArray(part) ? part : [part]))
    .filter(Boolean)
    .join(' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

function normalizePlanningSlug(slug: string | undefined) {
  const raw = String(slug ?? '').trim();

  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function makePlanningSlug(text: string) {
  return normalizePlanningSlug(text)
    .replace(/[״"']/g, '')
    .replace(/[\\/|]+/g, '-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export function getCityVenueGuideSlug(city: SeoCity | string) {
  return makePlanningSlug(`בחירת אולם ב ${String(city ?? '').trim()}`);
}

export function getLegacyCityVenueGuideSlug(city: SeoCity | string) {
  return makePlanningSlug(`בחירת אולם${withBet(String(city ?? '').trim())}`);
}

function resolveVenueGuideCity(
  city: SeoCity | string,
): Exclude<SeoCity, ''> | null {
  const cleanCity = String(city ?? '').trim();
  if (!cleanCity) return null;

  const match = (SEO_ALL_CITIES as readonly string[]).find(
    candidate => candidate === cleanCity,
  );
  return match ? (match as Exclude<SeoCity, ''>) : null;
}

function uniqueVenueNames(venues: readonly string[]) {
  const seen = new Set<string>();

  return venues
    .map(venue =>
      String(venue ?? '')
        .replace(/\s+/g, ' ')
        .trim(),
    )
    .filter(venue => {
      if (!venue || seen.has(venue)) return false;
      seen.add(venue);
      return true;
    });
}

function getVenueGuideData(city: SeoCity | string, i18n: I18nConstants) {
  const cityName = resolveVenueGuideCity(city);
  if (!cityName) return undefined;

  const venues = uniqueVenueNames(i18n.getCityVenues(cityName));
  if (venues.length < CITY_VENUE_GUIDE_MIN_VENUES) return undefined;

  return { city: cityName, venues };
}

function previewVenueNames(
  venues: readonly string[],
  limit = 6,
  moreLabel = 'ועוד',
) {
  const preview = venues.slice(0, limit).join(', ');
  return venues.length > limit ? `${preview} ${moreLabel}` : preview;
}

type PlanningFaqCategory =
  | 'chuppah'
  | 'slow'
  | 'recording'
  | 'music'
  | 'timeline'
  | 'attractions'
  | 'musicians'
  | 'entrance'
  | 'hennaTradition'
  | 'generic';

type LocalizedArticleFaqArgs = {
  key: PlanningArticleKey;
  title: string;
  serviceLabel: string;
  sectionTitle: string;
  bullets: string[];
  workBullets: string[];
  lang: string;
  eventLabel: string;
};

function buildArticleKeywords(
  articleTitle: string,
  serviceLabel: string,
  service: SeoService,
) {
  const secondaryKeyword = SECONDARY_KEYWORD_BY_SERVICE[service];
  const barBatPrimaryKeyword =
    service === SEO_SERVICE_BAR_BAT ? 'דיג׳יי לבר מצווה' : undefined;
  const barBatPrimaryAltKeyword =
    service === SEO_SERVICE_BAR_BAT ? 'דיג׳יי לבת מצווה' : undefined;
  const barBatExtraKeyword =
    service === SEO_SERVICE_BAR_BAT ? 'תקליטן לבת מצווה' : undefined;

  return [
    articleTitle,
    serviceLabel,
    barBatPrimaryKeyword,
    barBatPrimaryAltKeyword,
    secondaryKeyword,
    barBatExtraKeyword,
    `תכנון ${service}`,
    `מוזיקה ל${service}`,
    'אליאור מור',
  ].filter((keyword): keyword is string => Boolean(keyword));
}

function isHennaTraditionArticle(key: PlanningArticleKey) {
  return (
    key === 'henna_moroccan' ||
    key === 'henna_kurdish' ||
    key === 'henna_tunisian' ||
    key === 'henna_yemenite' ||
    key === 'henna_indian' ||
    key === 'henna_iraqi' ||
    key === 'henna_persian'
  );
}

function getPlanningFaqCategory(key: PlanningArticleKey): PlanningFaqCategory {
  if (key === 'wedding_chuppah_song') return 'chuppah';
  if (key === 'wedding_slow_song') return 'slow';
  if (key === 'bar_bat_recording') return 'recording';
  if (key.endsWith('_music')) return 'music';
  if (key.includes('timeline')) return 'timeline';
  if (key.includes('attractions')) return 'attractions';
  if (key.includes('musicians')) return 'musicians';
  if (key.includes('entrance')) return 'entrance';
  if (isHennaTraditionArticle(key)) return 'hennaTradition';

  return 'generic';
}

function getHebrewEventLabel(serviceLabel: string) {
  return serviceLabel
    .replace(/^די\s+ג[׳']?יי\s+ל/, '')
    .replace(/^דיג[׳']?יי\s+ל/, '')
    .replace(/^תקליטן\s+ל/, '');
}

function getPlanningFaqEventLabel(
  key: PlanningArticleKey,
  lang: string,
  serviceLabel: string,
) {
  if (lang === 'en') {
    if (key.startsWith('wedding_')) return 'a wedding';
    if (key.startsWith('henna_')) return 'a henna';
    if (key.startsWith('bar_bat_')) return 'a bar/bat mitzvah';
    return 'an event';
  }

  if (lang === 'fr') {
    if (key.startsWith('wedding_')) return 'un mariage';
    if (key.startsWith('henna_')) return 'un henné';
    if (key.startsWith('bar_bat_')) return 'une bar/bat mitzvah';
    return 'un événement';
  }

  if (lang === 'ru') {
    if (key.startsWith('wedding_')) return 'свадьбы';
    if (key.startsWith('henna_')) return 'хины';
    if (key.startsWith('bar_bat_')) return 'бар/бат-мицвы';
    return 'события';
  }

  return getHebrewEventLabel(serviceLabel);
}

function buildEnglishArticleFaqs(args: LocalizedArticleFaqArgs): FAQItem[] {
  const { key, title, sectionTitle, bullets, eventLabel } = args;
  const category = getPlanningFaqCategory(key);
  const firstTip = bullets[0] ?? sectionTitle;
  const secondTip = bullets[1] ?? firstTip;

  if (category === 'chuppah') {
    return [
      {
        q: 'How do you choose a chuppah entrance song that still feels personal?',
        a: 'Start with a song that genuinely fits the couple, then check the lyrics, opening, walking pace and emotional peak. A good edit, acoustic version or clean cut can make even a familiar song feel personal.',
      },
      {
        q: 'How long should the chuppah entrance song be?',
        a: 'The length depends on the walking distance, the number of people entering and the entrance order. In most ceremonies the full song is not needed, so it is better to prepare exact start and exit points.',
      },
      {
        q: 'Should musicians or cold fireworks be added to the chuppah entrance?',
        a: 'They can work well when they support the moment. Violin, saxophone, low smoke or cold fireworks should be approved by the venue and timed with the DJ, photographers and event manager.',
      },
      {
        q: 'What should be sent to the DJ before the chuppah?',
        a: 'Send the entrance order, exact song versions, names of escorts, start points and any planned effects. Clear information keeps the ceremony calm, respectful and precise.',
      },
    ];
  }

  if (category === 'slow') {
    return [
      {
        q: 'How do you choose a first dance song that feels personal?',
        a: 'Choose a song that feels true to the couple, not only the most expected choice. Check the tempo, lyrics and whether the song can be edited to the right length while keeping the emotion.',
      },
      {
        q: 'When should family and friends join the first dance?',
        a: 'If you want an intimate moment, keep the first part for the couple and invite guests later. If the goal is to open the dance floor quickly, plan an earlier join and a clean transition into a happy song.',
      },
      {
        q: 'Do balloons, low smoke or cold fireworks fit the first dance?',
        a: 'Yes, when the timing is accurate. Effects should enter at a clear musical peak, stay camera-friendly and keep the couple visible.',
      },
      {
        q: 'What does the DJ need to prepare for the first dance?',
        a: 'The DJ should prepare the right song version, start point, exit point and transition to the next song. If effects are involved, they should be coordinated with the venue, attraction operator and photographers.',
      },
    ];
  }

  if (category === 'recording') {
    return [
      {
        q: 'What kind of song should be recorded for a bar/bat mitzvah?',
        a: 'Choose a song the child genuinely likes and can make personal in a simple way. The song should fit the age, family and tone of the event.',
      },
      {
        q: 'When should work on a recorded song begin?',
        a: 'Start a few weeks before the event so there is time to choose the song, write lyrics, record calmly and decide how it fits the entrance, video or family moment.',
      },
      {
        q: 'Is a music video required for a recorded song?',
        a: 'No. Sometimes the song works better as an entrance or a short stage moment. If you do make a video, keep it focused and plan how the event returns to live energy afterward.',
      },
      {
        q: 'What should be coordinated with the DJ for a recorded song?',
        a: 'Send a high-quality final file, check volume, avoid multiple versions and decide what happens immediately after the song: blessing, entrance, game or dance floor opening.',
      },
    ];
  }

  if (category === 'music') {
    return [
      {
        q: `How do you choose music that truly fits ${eventLabel}?`,
        a: 'Start by mapping the guests, the family, the age groups and the atmosphere you want. The right music is not just a playlist, but a flow that can react to the crowd in real time.',
      },
      {
        q: 'Which music styles should be included?',
        a: 'You can combine Israeli, Mizrahi, Latin, hip hop, nostalgia, religious music, trance, techno and current hits. The important part is building smart transitions instead of forcing every style into the night.',
      },
      {
        q: 'How many songs should be sent to the DJ before the event?',
        a: 'Send a short must-play list, songs you love and a do-not-play list. A very long list can limit crowd reading, so it is better to give a clear direction and leave room for the DJ to lead.',
      },
      {
        q: `What is the difference between good music and professional music planning for ${eventLabel}?`,
        a: 'Good music is songs people like. Professional planning means knowing when to play them, in what order, at what volume and how to move between different audiences while keeping the event connected.',
      },
    ];
  }

  if (category === 'timeline') {
    return [
      {
        q: `How do you build the right timeline for ${eventLabel}?`,
        a: 'Divide the evening into stages: reception, entrance or ceremony, food, speeches and dancing. Each stage needs realistic timing, matching music and a clear transition to the next moment.',
      },
      {
        q: 'How much buffer time should be left between parts of the event?',
        a: 'Leave small buffers for natural delays, but avoid long gaps that lower the energy. The timeline should be flexible enough for the venue and clear enough for every supplier.',
      },
      {
        q: 'Who should receive the timeline before the event?',
        a: 'The DJ, venue manager, photographers, host and every supplier responsible for an attraction or key moment. When everyone works from the same timeline, the event feels smoother.',
      },
      {
        q: 'How do you know if the timeline needs simplifying?',
        a: 'If many entrances, speeches, videos and attractions are placed back to back, the evening can feel less clear. A good timeline leaves space for food, conversation and dancing.',
      },
    ];
  }

  if (category === 'attractions') {
    return [
      {
        q: `Which attractions actually fit ${eventLabel}?`,
        a: 'Attractions should match the crowd, venue and tone of the evening. Cold fireworks, smoke, balloons, lasers, LED letters or photo stations can work well when they enter at the right moment.',
      },
      {
        q: 'How do you make attractions support the dance floor?',
        a: 'Choose each attraction for a real moment in the event. Check where it stands, how long it takes, whether it affects photos or dancing and what music should support it.',
      },
      {
        q: 'When should fireworks, smoke, lasers or balloons be activated?',
        a: 'Use them at a clear highlight: entrance, first dance, dance-floor opening, musical drop or after party. Good timing makes the effect feel emotional and connected.',
      },
      {
        q: 'What must be checked with the venue before booking an attraction?',
        a: 'Check approvals, safety rules, electricity, ceiling height, setup time and photography impact. Some venues restrict smoke, fireworks or specific equipment.',
      },
    ];
  }

  if (category === 'musicians') {
    return [
      {
        q: `When should live musicians be added to ${eventLabel}?`,
        a: 'Musicians work best when they strengthen a specific moment: reception, entrance, ceremony, dance-floor opening or a short highlight. A clear role helps them blend naturally with the DJ.',
      },
      {
        q: 'Which musician fits which kind of moment?',
        a: 'Saxophone fits reception or dancing, violin can support an emotional moment, bouzouki or guitar can add warmth, drummers can create a joyful opening, and singers or a live band can create a bigger live moment.',
      },
      {
        q: 'What should be coordinated between musicians and the DJ?',
        a: 'Coordinate songs, keys, entry points, performance length, sound connections and transitions. A good musician sounds best when they know exactly when to join and when to let the DJ lead.',
      },
      {
        q: 'Do musicians fit an event with a young crowd?',
        a: 'Yes, if the live part is short and focused. With a young crowd, the musician should add a strong moment that connects back to the party.',
      },
    ];
  }

  if (category === 'entrance') {
    if (key === 'henna_entrance') {
      return [
        {
          q: 'How do you plan a good henna entrance?',
          a: 'Start with the couple, the family tradition and the entrance song. Decide who enters with the groom and bride, where everyone stands, and what happens immediately after the entrance.',
        },
        {
          q: 'How do you choose a henna entrance song?',
          a: 'Choose a song that fits the family origin, the entrance pace and the energy you want in the room. The song should feel familiar enough for the family and still lift the evening.',
        },
        {
          q: 'What should happen after the henna entrance?',
          a: 'Prepare a clear transition into the henna application ceremony, a family moment, an outfit change or a short dance section. The entrance should open the evening and connect to what comes next.',
        },
        {
          q: 'What should be sent to the DJ before the entrance?',
          a: 'Send the entrance order, song choice, start point, announcement text and any family details that matter. Clear coordination helps the entrance feel natural and organized.',
        },
      ];
    }

    return [
      {
        q: `How do you plan an impressive entrance for ${eventLabel}?`,
        a: 'A strong entrance starts with the right song, a clear order and precise timing. Decide who enters, where they enter from, how long the walk takes and what happens immediately afterward.',
      },
      {
        q: 'Should drums, smoke or cold fireworks be added to the entrance?',
        a: 'They can work well when they fit the venue and the crowd. The effect should enter on a musical peak, stay camera-friendly and avoid creating a technical delay.',
      },
      {
        q: 'How long should an entrance last?',
        a: 'An entrance should be short, clear and accurate. Match the song length to the walking distance and number of people entering so the moment has enough space and keeps the crowd with it.',
      },
      {
        q: 'What should be sent to the DJ before the entrance?',
        a: 'Send the entrance order, song, start point, announcement text if there is one and any planned effects. The clearer the coordination, the more natural and professional the entrance looks.',
      },
    ];
  }

  if (category === 'hennaTradition') {
    if (key === 'henna_timeline') {
      return [
        {
          q: 'What should a henna timeline include?',
          a: 'A henna timeline should include entrances, outfit changes, the henna application ceremony, family moments, food courses between entrances and dancing.',
        },
        {
          q: 'How do you keep a henna flowing well?',
          a: 'Plan the transitions in advance and decide where the DJ raises the tempo. The goal is to respect the family moments and keep the evening moving naturally.',
        },
        {
          q: 'How do food courses fit into a henna timeline?',
          a: 'Food courses should sit naturally between entrances, ceremony moments and dancing. When the timing is clear, guests stay with the flow of the evening.',
        },
        {
          q: 'Why is DJ coordination important in a henna timeline?',
          a: 'The DJ prepares the music for every transition: entrance, ceremony, outfit change, food, family moments and dance floor. That is what keeps the evening connected.',
        },
      ];
    }

    if (key === 'henna_attractions') {
      return [
        {
          q: 'Which attractions fit a henna celebration?',
          a: 'Popular henna attractions include a fruit display, belly dancer, cocktail bar, magnet photographer, special photo stations, unique souvenirs and guest outfits for the ceremony.',
        },
        {
          q: 'How do you choose attractions for a henna?',
          a: 'Choose attractions that fit the family style, the type of henna and the venue. A Moroccan, Persian, Indian, Yemenite, Tunisian or Iraqi henna can each need a different feel.',
        },
        {
          q: 'When should henna attractions be used?',
          a: 'Place them around the entrance, henna application ceremony, family moments, outfit changes or dance floor opening. Timing matters more than quantity.',
        },
        {
          q: 'How many attractions should a henna include?',
          a: 'A few well-timed attractions usually work better than many small stations. Choose ideas that support the ceremony, photos and dance floor.',
        },
      ];
    }

    if (key === 'henna_musicians') {
      return [
        {
          q: 'Which musicians fit a henna?',
          a: 'Common options include a darbuka player for henna, violinist for henna, saxophone, bouzouki, guitar, darbukas, drummers, keyboards, singers or a live band.',
        },
        {
          q: 'How do musicians work with the DJ in a henna?',
          a: 'Coordinate songs, rhythms, entry points, microphones, monitor and volume in advance. The musicians should support the ceremony and dance floor and stay connected to the DJ set.',
        },
        {
          q: 'Do musicians fit every type of henna?',
          a: 'They can fit Moroccan, Persian, Indian, Yemenite, Tunisian, Iraqi, Kurdish or mixed henna celebrations when the instrument matches the family style.',
        },
        {
          q: 'When should musicians play during a henna?',
          a: 'They usually work best around the entrance, ceremony moments, family circles or a short live highlight on the dance floor.',
        },
      ];
    }

    return [
      {
        q: `What is most important when planning ${title}?`,
        a: 'Keep the family and traditional identity clear while building a happy dance floor for young and older guests. The right balance gives the ceremony respect and keeps the celebration warm.',
      },
      {
        q: `How do you combine tradition with current music in ${title}?`,
        a: 'Start with songs and rhythms the family recognizes, then move gradually into Mizrahi, Israeli, current hits and dance-floor music. Smooth transitions keep the evening natural.',
      },
      {
        q: `What details should be coordinated in advance for ${title}?`,
        a: 'Coordinate entrances, outfits, the henna application ceremony, family moments, food timing, attractions and musical cues. A henna has many small moments, and planning connects them into one evening.',
      },
      {
        q: `Does every DJ fit ${title}?`,
        a: 'Not always. A henna DJ should understand tradition, family dynamics and the dance floor, knowing when to honor the ceremony and when to open the rhythm.',
      },
    ];
  }

  return [
    {
      q: `How do you know if ${title} fits my event?`,
      a: `Check whether the idea fits the guests, venue, timing and your personal style. A good starting point is: ${firstTip}`,
    },
    {
      q: `When should ${title} be planned?`,
      a: `Plan it while building the event timeline, then refine it once the guest profile, venue setup and supplier needs are clear. That keeps ${title} connected to the event flow.`,
    },
    {
      q: `What should be coordinated with the DJ for ${title}?`,
      a: 'Coordinate timing, song versions, announcements, technical needs, supplier cues and the transition into the next moment so the music and event flow work together.',
    },
    {
      q: `What mistake should be avoided when planning ${title}?`,
      a: `Avoid choosing only by trend without checking if it fits the people in the room and the flow of the evening. Start from: ${secondTip}`,
    },
  ];
}

function buildFrenchArticleFaqs(args: LocalizedArticleFaqArgs): FAQItem[] {
  const { key, title, sectionTitle, bullets, eventLabel } = args;
  const category = getPlanningFaqCategory(key);
  const firstTip = bullets[0] ?? sectionTitle;
  const secondTip = bullets[1] ?? firstTip;

  if (category === 'chuppah') {
    return [
      {
        q: 'Comment choisir une chanson d’entrée à la houppa qui reste personnelle ?',
        a: 'Commencez par une chanson qui vous ressemble vraiment, puis vérifiez les paroles, l’introduction, le rythme de marche et le moment fort. Une bonne version ou un montage précis peut rendre le choix beaucoup plus personnel.',
      },
      {
        q: 'Combien de temps doit durer la chanson d’entrée à la houppa ?',
        a: 'La durée dépend de la distance, du nombre d’entrées et de l’ordre prévu. Dans la plupart des cérémonies, il vaut mieux préparer un point de départ et un point de sortie précis plutôt que jouer toute la chanson.',
      },
      {
        q: 'Faut-il ajouter des musiciens ou des feux froids à l’entrée ?',
        a: 'Oui si cela sert le moment. Violon, saxophone, fumée basse ou feux froids doivent être validés par le lieu et coordonnés avec le DJ, les photographes et le responsable de l’événement.',
      },
      {
        q: 'Que faut-il transmettre au DJ avant la houppa ?',
        a: 'Envoyez l’ordre des entrées, les versions exactes des chansons, les noms des accompagnants, les points de départ et les effets prévus. Plus l’information est claire, plus la cérémonie reste fluide.',
      },
    ];
  }

  if (category === 'slow') {
    return [
      {
        q: 'Comment choisir une première danse qui vous ressemble ?',
        a: 'Choisissez une chanson qui vous parle vraiment, pas seulement le choix le plus attendu. Vérifiez le tempo, les paroles et la possibilité de raccourcir la chanson en gardant l’émotion.',
      },
      {
        q: 'Quand inviter la famille et les amis à rejoindre la première danse ?',
        a: 'Pour un moment intime, gardez la première partie pour le couple puis invitez les proches progressivement. Pour ouvrir vite la piste, préparez une entrée plus rapide des invités et une transition vers un titre joyeux.',
      },
      {
        q: 'Les ballons, la fumée basse ou les feux froids conviennent-ils à la première danse ?',
        a: 'Oui, avec un bon timing. Les effets doivent entrer sur un vrai moment fort, rester adaptés aux photos et garder le couple bien visible.',
      },
      {
        q: 'Que doit préparer le DJ pour la première danse ?',
        a: 'Le DJ doit préparer la bonne version, le point d’entrée, le point de sortie et la transition vers le morceau suivant. Les effets doivent aussi être coordonnés à l’avance.',
      },
    ];
  }

  if (category === 'recording') {
    return [
      {
        q: 'Quel type de chanson enregistrer pour une bar/bat mitzvah ?',
        a: 'Choisissez une chanson que l’enfant aime vraiment et qui peut devenir personnelle de façon simple. Elle doit convenir à son âge, à la famille et à l’ambiance de la soirée.',
      },
      {
        q: 'Quand commencer à travailler sur une chanson enregistrée ?',
        a: 'Commencez quelques semaines avant l’événement pour choisir le titre, écrire les paroles, enregistrer calmement et décider comment l’intégrer à l’entrée, à une vidéo ou à un moment familial.',
      },
      {
        q: 'Faut-il obligatoirement faire un clip ?',
        a: 'Non. Parfois la chanson fonctionne mieux comme entrée ou court moment sur scène. Si vous faites un clip, gardez-le concentré et préparez le retour à l’énergie de la salle.',
      },
      {
        q: 'Que faut-il coordonner avec le DJ pour une chanson enregistrée ?',
        a: 'Envoyez un fichier final de bonne qualité, vérifiez le volume, évitez plusieurs versions et décidez ce qui se passe juste après la chanson.',
      },
    ];
  }

  if (category === 'music') {
    return [
      {
        q: `Comment choisir une musique vraiment adaptée à ${eventLabel} ?`,
        a: 'Commencez par comprendre les invités, les familles, les âges et l’ambiance voulue. Une bonne musique n’est pas seulement une playlist, c’est un déroulé capable de réagir au public.',
      },
      {
        q: 'Quels styles musicaux faut-il intégrer ?',
        a: 'On peut mélanger israélien, mizrahi, latino, hip hop, nostalgie, musique religieuse, trance, techno et tubes actuels. L’essentiel est de créer des transitions intelligentes.',
      },
      {
        q: 'Combien de chansons envoyer au DJ avant l’événement ?',
        a: 'Envoyez une courte liste obligatoire, des chansons aimées et une liste à éviter. Une liste très longue limite la lecture du public, donc mieux vaut donner une direction claire.',
      },
      {
        q: `Quelle est la différence entre bonne musique et vraie planification musicale pour ${eventLabel} ?`,
        a: 'Une bonne musique, ce sont des chansons qu’on aime. Une planification professionnelle consiste à savoir quand les jouer, dans quel ordre, à quel volume et comment passer d’un public à l’autre.',
      },
    ];
  }

  if (category === 'timeline') {
    return [
      {
        q: `Comment construire un bon déroulé pour ${eventLabel} ?`,
        a: 'Divisez la soirée en étapes : accueil, entrée ou cérémonie, repas, discours et danse. Chaque étape a besoin d’un timing réaliste, d’une musique adaptée et d’une transition claire.',
      },
      {
        q: 'Combien de marge laisser entre les parties ?',
        a: 'Gardez de petites marges pour les retards naturels et un ordre clair que tous les prestataires comprennent. Le planning doit rester souple mais facile à exécuter.',
      },
      {
        q: 'Qui doit recevoir le planning avant l’événement ?',
        a: 'Le DJ, le responsable de salle, les photographes, l’animateur et chaque prestataire lié à un moment important. Tout le monde travaille mieux avec la même version.',
      },
      {
        q: 'Comment savoir si le planning doit être simplifié ?',
        a: 'Si beaucoup d’entrées, discours, vidéos et attractions sont placés à la suite, la soirée peut devenir moins lisible. Un bon planning laisse de la place au repas, aux échanges et à la danse.',
      },
    ];
  }

  if (category === 'attractions') {
    return [
      {
        q: `Quelles attractions conviennent vraiment à ${eventLabel} ?`,
        a: 'Les attractions doivent correspondre au public, au lieu et au style de la soirée. Feux froids, fumée, ballons, lasers, lettres lumineuses ou stands photo fonctionnent bien quand ils arrivent au bon moment.',
      },
      {
        q: 'Comment faire en sorte que les attractions soutiennent la piste ?',
        a: 'Choisissez chaque attraction pour un vrai moment de la soirée. Vérifiez son emplacement, sa durée, son impact sur les photos et la musique qui doit l’accompagner.',
      },
      {
        q: 'Quand activer feux froids, fumée, lasers ou ballons ?',
        a: 'Au moment d’un vrai point fort : entrée, première danse, ouverture de piste, drop musical ou after-party. Un bon timing rend l’effet naturel et émouvant.',
      },
      {
        q: 'Que vérifier avec le lieu avant de réserver une attraction ?',
        a: 'Vérifiez autorisations, sécurité, électricité, hauteur de plafond, temps de montage et impact sur les photos. Certains lieux limitent fumée, feux ou équipements spécifiques.',
      },
    ];
  }

  if (category === 'musicians') {
    return [
      {
        q: `Quand ajouter des musiciens live à ${eventLabel} ?`,
        a: 'Les musiciens sont les plus utiles quand ils renforcent un moment précis : accueil, entrée, cérémonie, ouverture de piste ou court temps fort. Un rôle clair les aide à se fondre naturellement avec le DJ.',
      },
      {
        q: 'Quel musicien convient à quel moment ?',
        a: 'Le saxophone convient à l’accueil ou à la piste, le violon renforce un moment émouvant, le bouzouki ou la guitare ajoutent de la chaleur, les batteurs ouvrent une ambiance joyeuse et chanteurs ou groupe live créent un moment plus grand.',
      },
      {
        q: 'Que faut-il coordonner entre les musiciens et le DJ ?',
        a: 'Il faut coordonner morceaux, tonalités, points d’entrée, durée, connexions son et transitions. Un musicien sonne mieux quand il sait exactement quand entrer.',
      },
      {
        q: 'Les musiciens conviennent-ils à un public jeune ?',
        a: 'Oui, si le passage est court et précis. Avec un public jeune, le musicien doit ajouter un moment fort qui revient naturellement à la fête.',
      },
    ];
  }

  if (category === 'entrance') {
    if (key === 'henna_entrance') {
      return [
        {
          q: 'Comment planifier une bonne entrée de henné ?',
          a: 'Commencez par le couple, la tradition familiale et la chanson d’entrée. Décidez qui entre avec le fiancé et la fiancée, où chacun se place et ce qui arrive juste après.',
        },
        {
          q: 'Comment choisir une chanson d’entrée de henné ?',
          a: 'Choisissez une chanson adaptée à l’origine familiale, au rythme d’entrée et à l’énergie voulue dans la salle. Elle doit parler à la famille et ouvrir l’ambiance.',
        },
        {
          q: 'Que doit-il se passer après l’entrée de henné ?',
          a: 'Préparez une transition claire vers l’application du henné, un moment familial, un changement de tenue ou une courte partie dansante. L’entrée doit ouvrir la soirée et se connecter à la suite.',
        },
        {
          q: 'Que faut-il transmettre au DJ avant l’entrée ?',
          a: 'Envoyez l’ordre d’entrée, la chanson, le point de départ, le texte d’annonce et les détails familiaux importants. Une coordination claire rend l’entrée naturelle.',
        },
      ];
    }

    return [
      {
        q: `Comment planifier une entrée marquante pour ${eventLabel} ?`,
        a: 'Une bonne entrée commence par la bonne chanson, un ordre clair et un timing précis. Décidez qui entre, d’où, combien de temps cela prend et ce qui arrive juste après.',
      },
      {
        q: 'Faut-il ajouter tambours, fumée ou feux froids à l’entrée ?',
        a: 'Oui si cela convient au lieu et au public. L’effet doit arriver sur un moment fort de la musique, rester compatible avec les photos et être prêt techniquement avant l’entrée.',
      },
      {
        q: 'Combien de temps doit durer une entrée ?',
        a: 'Une entrée doit être courte, claire et précise. Adaptez la chanson à la distance et au nombre d’entrants pour donner au moment assez d’espace et garder le public avec vous.',
      },
      {
        q: 'Que faut-il transmettre au DJ avant l’entrée ?',
        a: 'Envoyez l’ordre d’entrée, la chanson, le point de départ, l’annonce éventuelle et les effets prévus. Une coordination claire rend l’entrée plus naturelle et professionnelle.',
      },
    ];
  }

  if (category === 'hennaTradition') {
    if (key === 'henna_timeline') {
      return [
        {
          q: 'Que doit inclure le planning d’une henné ?',
          a: 'Il doit inclure entrées, changements de tenues, application du henné, moments familiaux, plats entre les entrées et moments de danse.',
        },
        {
          q: 'Comment garder une henné fluide ?',
          a: 'Planifiez les transitions à l’avance et décidez avec le DJ où faire monter le rythme. Le but est de respecter les moments familiaux et de garder une soirée naturelle.',
        },
        {
          q: 'Comment placer les plats dans le planning ?',
          a: 'Les plats doivent se placer naturellement entre entrées, cérémonie et danse. Quand le timing est clair, les invités restent dans le déroulé.',
        },
        {
          q: 'Pourquoi la coordination DJ est-elle importante ?',
          a: 'Le DJ prépare la musique pour chaque transition : entrée, cérémonie, changement de tenue, repas, moments familiaux et piste. C’est ce qui garde la soirée connectée.',
        },
      ];
    }

    if (key === 'henna_attractions') {
      return [
        {
          q: 'Quelles animations conviennent à une henné ?',
          a: 'Présentation de fruits, danseuse orientale, bar à cocktails, photographe magnets, stands photo, souvenirs personnalisés et tenues pour les invités peuvent convenir selon le style familial.',
        },
        {
          q: 'Comment choisir les animations de henné ?',
          a: 'Choisissez selon le style familial, le type de henné et le lieu. Une henné marocaine, persane, indienne, yéménite, tunisienne ou irakienne peut demander une ambiance différente.',
        },
        {
          q: 'Quand intégrer les animations ?',
          a: 'Autour de l’entrée, de l’application du henné, des moments familiaux, des changements de tenues ou de l’ouverture de piste. Le timing compte plus que la quantité.',
        },
        {
          q: 'Combien d’animations faut-il prévoir ?',
          a: 'Quelques animations bien placées fonctionnent souvent mieux qu’une longue liste. Choisissez celles qui servent la cérémonie, les photos et la piste.',
        },
      ];
    }

    if (key === 'henna_musicians') {
      return [
        {
          q: 'Quels musiciens conviennent à une henné ?',
          a: 'Les options courantes sont joueur de darbouka, violoniste, saxophone, bouzouki, guitare, darboukas, batteurs, claviers, chanteurs ou groupe live.',
        },
        {
          q: 'Comment les musiciens travaillent-ils avec le DJ ?',
          a: 'Coordonnez chansons, rythmes, points d’entrée, micros, retour et volume. Les musiciens doivent soutenir la cérémonie et la piste en restant connectés au set du DJ.',
        },
        {
          q: 'Les musiciens conviennent-ils à tous les types de henné ?',
          a: 'Oui, si l’instrument correspond au style familial : henné marocaine, persane, indienne, yéménite, tunisienne, irakienne, kurde ou mixte.',
        },
        {
          q: 'Quand faire jouer les musiciens ?',
          a: 'Ils fonctionnent surtout autour de l’entrée, des moments de cérémonie, des rondes familiales ou d’un court temps fort live sur la piste.',
        },
      ];
    }

    return [
      {
        q: `Quel est le point le plus important dans ${title} ?`,
        a: 'Il faut garder l’identité familiale et traditionnelle tout en construisant une piste joyeuse. Le bon équilibre respecte la cérémonie et garde la soirée chaleureuse.',
      },
      {
        q: `Comment mélanger tradition et musique actuelle dans ${title} ?`,
        a: 'Commencez par les chansons et rythmes connus de la famille, puis passez progressivement vers mizrahi, israélien, tubes actuels et musique de piste.',
      },
      {
        q: `Quels détails coordonner à l’avance pour ${title} ?`,
        a: 'Coordonnez entrées, tenues, application du henné, moments familiaux, timing des plats, animations et repères musicaux. Une henné contient beaucoup de petits moments qui doivent se connecter.',
      },
      {
        q: `Tous les DJ conviennent-ils à ${title} ?`,
        a: 'Pas toujours. Un DJ de henné doit comprendre la tradition, la famille et la piste, et savoir quand respecter le rituel puis quand ouvrir le rythme.',
      },
    ];
  }

  return [
    {
      q: `Comment savoir si ${title} convient à mon événement ?`,
      a: `Vérifiez si l’idée correspond aux invités, au lieu, au timing et à votre style. Un bon point de départ : ${firstTip}`,
    },
    {
      q: `Quand faut-il planifier ${title} ?`,
      a: `Planifiez-le pendant la construction du déroulé, puis ajustez quand le public, le lieu et les prestataires sont clairs. Ainsi ${title} reste lié au rythme de la soirée.`,
    },
    {
      q: `Que faut-il coordonner avec le DJ pour ${title} ?`,
      a: 'Coordonnez timing, versions des chansons, annonces, besoins techniques, repères des prestataires et transition vers le moment suivant.',
    },
    {
      q: `Quelle erreur éviter avec ${title} ?`,
      a: `Ne choisissez pas seulement selon une tendance. Vérifiez si l’idée convient aux invités et au déroulé, puis commencez par : ${secondTip}`,
    },
  ];
}

function buildRussianArticleFaqs(args: LocalizedArticleFaqArgs): FAQItem[] {
  const { key, title, sectionTitle, bullets, eventLabel } = args;
  const category = getPlanningFaqCategory(key);
  const firstTip = bullets[0] ?? sectionTitle;
  const secondTip = bullets[1] ?? firstTip;

  if (category === 'chuppah') {
    return [
      {
        q: 'Как выбрать песню для входа к хупе, чтобы она звучала лично?',
        a: 'Начните с песни, которая действительно подходит паре, затем проверьте текст, вступление, темп ходьбы и эмоциональный пик. Хорошая версия или точный монтаж делают момент более личным.',
      },
      {
        q: 'Сколько должна длиться песня для входа к хупе?',
        a: 'Длина зависит от расстояния, количества входящих и порядка входа. Чаще всего не нужно включать всю песню, лучше подготовить точные точки старта и выхода.',
      },
      {
        q: 'Стоит ли добавлять музыкантов или холодные фейерверки?',
        a: 'Да, если это усиливает момент. Скрипка, саксофон, низкий дым или холодные фейерверки должны быть согласованы с площадкой, DJ, фотографами и менеджером события.',
      },
      {
        q: 'Что передать DJ перед хупой?',
        a: 'Передайте порядок входов, точные версии песен, имена сопровождающих, точки старта и все эффекты. Чем яснее информация, тем спокойнее и точнее проходит церемония.',
      },
    ];
  }

  if (category === 'slow') {
    return [
      {
        q: 'Как выбрать песню для первого танца, чтобы момент был личным?',
        a: 'Выбирайте песню, которая действительно связана с парой, а не только самый ожидаемый вариант. Проверьте темп, текст и возможность сократить песню, сохранив эмоцию.',
      },
      {
        q: 'Когда приглашать семью и друзей присоединиться к первому танцу?',
        a: 'Если нужен интимный момент, оставьте первую часть только для пары и пригласите близких позже. Если важно быстро открыть танцпол, заранее подготовьте переход в более радостную песню.',
      },
      {
        q: 'Подходят ли шары, низкий дым или холодные фейерверки для первого танца?',
        a: 'Да, при точном тайминге. Эффекты должны входить в музыкальный пик, не мешать съемке и оставлять пару в центре внимания.',
      },
      {
        q: 'Что DJ должен подготовить для первого танца?',
        a: 'Нужны правильная версия песни, точка входа, точка выхода и переход к следующему треку. Если есть эффекты, их согласуют заранее с площадкой и фотографами.',
      },
    ];
  }

  if (category === 'recording') {
    return [
      {
        q: 'Какую песню записать для бар/бат-мицвы?',
        a: 'Выберите песню, которая действительно нравится ребенку и может стать личной простым способом. Она должна подходить возрасту, семье и характеру вечера.',
      },
      {
        q: 'Когда начинать работу над записанной песней?',
        a: 'Лучше начать за несколько недель до события, чтобы выбрать песню, написать текст, спокойно записать и решить, как встроить ее во вход, видео или семейный момент.',
      },
      {
        q: 'Обязательно ли делать клип для записанной песни?',
        a: 'Нет. Иногда песня лучше работает как вход или короткий сценический момент. Если делаете клип, держите его коротким и заранее планируйте возвращение к живой энергии.',
      },
      {
        q: 'Что согласовать с DJ по записанной песне?',
        a: 'Передайте финальный файл в хорошем качестве, проверьте громкость, избегайте нескольких версий и решите, что происходит сразу после песни.',
      },
    ];
  }

  if (category === 'music') {
    return [
      {
        q: `Как выбрать музыку, которая действительно подходит для ${eventLabel}?`,
        a: 'Начните с гостей, семьи, возрастов и желаемой атмосферы. Хорошая музыка — это не только плейлист, а поток, который умеет реагировать на зал в реальном времени.',
      },
      {
        q: 'Какие музыкальные стили стоит включить?',
        a: 'Можно сочетать израильскую музыку, мизрахи, латину, хип-хоп, ностальгию, религиозную музыку, транс, техно и актуальные хиты. Главное — умные переходы, а не попытка включить все подряд.',
      },
      {
        q: 'Сколько песен отправить DJ до события?',
        a: 'Отправьте короткий must-play список, песни, которые вам нравятся, и do-not-play список. Очень длинный список мешает чтению зала, поэтому лучше дать направление и оставить место для работы DJ.',
      },
      {
        q: `В чем разница между хорошей музыкой и профессиональным музыкальным планированием для ${eventLabel}?`,
        a: 'Хорошая музыка — это любимые песни. Профессиональное планирование — понимать, когда их включать, в каком порядке, на какой громкости и как переходить между разными аудиториями.',
      },
    ];
  }

  if (category === 'timeline') {
    return [
      {
        q: `Как построить правильный тайминг для ${eventLabel}?`,
        a: 'Разделите вечер на этапы: прием гостей, вход или церемония, еда, речи и танцы. У каждого этапа должны быть реалистичное время, подходящая музыка и понятный переход.',
      },
      {
        q: 'Сколько запаса оставлять между частями события?',
        a: 'Оставьте небольшие буферы для естественных задержек, но не создавайте длинные паузы, которые снижают энергию. Тайминг должен быть гибким для площадки и понятным для поставщиков.',
      },
      {
        q: 'Кто должен получить тайминг до события?',
        a: 'DJ, менеджер площадки, фотографы, ведущий и каждый поставщик, отвечающий за важный момент или аттракцию. Одна общая версия уменьшает путаницу.',
      },
      {
        q: 'Как понять, что тайминг стоит упростить?',
        a: 'Если подряд стоят много входов, речей, видео и аттракций, вечер может стать менее понятным. Хороший тайминг оставляет место для еды, общения и танцев.',
      },
    ];
  }

  if (category === 'attractions') {
    return [
      {
        q: `Какие аттракции действительно подходят для ${eventLabel}?`,
        a: 'Развлечения должны подходить гостям, площадке и стилю вечера. Холодные фейерверки, дым, шары, лазеры, светящиеся буквы или фотозоны хорошо работают, когда появляются в правильный момент.',
      },
      {
        q: 'Как сделать так, чтобы развлечения поддерживали танцпол?',
        a: 'Выбирайте каждую идею под конкретный момент вечера. Проверьте место, длительность, влияние на съемку и музыку, которая должна ее поддержать.',
      },
      {
        q: 'Когда включать фейерверки, дым, лазеры или шары?',
        a: 'Включайте их в ясный пик: вход, первый танец, открытие танцпола, музыкальный drop или after party. Хороший тайминг делает эффект естественным и эмоциональным.',
      },
      {
        q: 'Что проверить с площадкой до заказа аттракции?',
        a: 'Проверьте разрешения, безопасность, электричество, высоту потолка, время монтажа и влияние на съемку. Некоторые площадки ограничивают дым, фейерверки или оборудование.',
      },
    ];
  }

  if (category === 'musicians') {
    return [
      {
        q: `Когда добавлять живых музыкантов для ${eventLabel}?`,
        a: 'Музыканты лучше всего работают, когда усиливают конкретный момент: прием гостей, вход, церемонию, открытие танцпола или короткий пик. Чёткая роль помогает им естественно сочетаться с DJ.',
      },
      {
        q: 'Какой музыкант подходит к какому моменту?',
        a: 'Саксофон подходит для встречи гостей или танцев, скрипка усиливает эмоциональный момент, бузуки или гитара добавляют тепло, барабанщики подходят для радостного открытия, а певцы или живая группа создают более крупный live-момент.',
      },
      {
        q: 'Что согласовать между музыкантами и DJ?',
        a: 'Согласуйте песни, тональности, точки входа, длительность выступления, подключение звука и переходы. Музыкант звучит лучше, когда точно знает, когда вступать.',
      },
      {
        q: 'Подходят ли музыканты для молодой аудитории?',
        a: 'Да, если живой момент короткий и точный. Для молодой аудитории музыкант должен добавить сильный эпизод, который естественно возвращается к вечеринке.',
      },
    ];
  }

  if (category === 'entrance') {
    if (key === 'henna_entrance') {
      return [
        {
          q: 'Как спланировать хороший вход на хину?',
          a: 'Начните с пары, семейной традиции и песни для входа. Решите, кто входит с женихом и невестой, где стоит семья и что происходит сразу после.',
        },
        {
          q: 'Как выбрать песню для входа на хину?',
          a: 'Выберите песню, которая подходит происхождению семьи, темпу входа и нужной энергии в зале. Она должна быть близкой семье и открывать атмосферу.',
        },
        {
          q: 'Что должно быть после входа на хину?',
          a: 'Подготовьте ясный переход к нанесению хны, семейному моменту, смене наряда или короткой танцевальной части. Вход должен открывать вечер и соединяться с продолжением.',
        },
        {
          q: 'Что передать DJ до входа?',
          a: 'Передайте порядок входа, песню, точку старта, текст объявления и важные семейные детали. Чёткая координация делает вход естественным.',
        },
      ];
    }

    return [
      {
        q: `Как спланировать эффектный вход для ${eventLabel}?`,
        a: 'Сильный вход начинается с правильной песни, ясного порядка и точного тайминга. Решите, кто входит, откуда, сколько занимает проход и что происходит сразу после.',
      },
      {
        q: 'Стоит ли добавлять барабаны, дым или холодные фейерверки?',
        a: 'Да, если это подходит площадке и гостям. Эффект должен входить в музыкальный пик, не мешать съемке и не создавать техническую задержку.',
      },
      {
        q: 'Сколько должен длиться вход?',
        a: 'Вход должен быть коротким, ясным и точным. Подберите длину песни под расстояние и количество входящих, чтобы момент получил достаточно места и держал внимание гостей.',
      },
      {
        q: 'Что передать DJ до входа?',
        a: 'Передайте порядок входа, песню, точку старта, текст объявления при необходимости и все эффекты. Чем яснее координация, тем естественнее и профессиональнее выглядит вход.',
      },
    ];
  }

  if (category === 'hennaTradition') {
    if (key === 'henna_timeline') {
      return [
        {
          q: 'Что должен включать тайминг хины?',
          a: 'Тайминг должен включать входы, смену нарядов, нанесение хны, семейные моменты, блюда между входами и танцы.',
        },
        {
          q: 'Как сохранить хороший ход хины?',
          a: 'Заранее спланируйте переходы и решите с DJ, где поднять темп. Цель — уважить семейные моменты и сохранить естественный ход вечера.',
        },
        {
          q: 'Как блюда вписываются в тайминг?',
          a: 'Блюда должны естественно располагаться между входами, церемонией и танцами. Когда тайминг понятен, гости остаются в ходе вечера.',
        },
        {
          q: 'Почему важна координация с DJ?',
          a: 'DJ готовит музыку для каждого перехода: вход, церемония, смена наряда, еда, семейные моменты и танцпол. Это сохраняет вечер цельным.',
        },
      ];
    }

    if (key === 'henna_attractions') {
      return [
        {
          q: 'Какие аттракции подходят для хины?',
          a: 'Подходят фруктовая подача, танцовщица живота, коктейль-бар, фотограф на магниты, фотозоны, памятные сувениры и наряды для гостей, если они соответствуют стилю семьи.',
        },
        {
          q: 'Как выбрать аттракции для хины?',
          a: 'Выбирайте по стилю семьи, типу хины и площадке. Марокканская, персидская, индийская, йеменская, тунисская или иракская хина могут требовать разного настроения.',
        },
        {
          q: 'Когда включать аттракции?',
          a: 'Вокруг входа, нанесения хны, семейных моментов, смены нарядов или открытия танцпола. Тайминг важнее количества.',
        },
        {
          q: 'Сколько развлечений стоит включить?',
          a: 'Несколько хорошо поставленных идей обычно работают лучше длинного списка. Выбирайте то, что поддерживает церемонию, фото и танцпол.',
        },
      ];
    }

    if (key === 'henna_musicians') {
      return [
        {
          q: 'Какие музыканты подходят для хины?',
          a: 'Частые варианты: дарбукист на хину, скрипачка на хину, саксофон, бузуки, гитара, дарбуки, барабанщики, клавиши, певцы или живая группа.',
        },
        {
          q: 'Как музыканты работают с DJ?',
          a: 'Согласуйте песни, ритмы, точки входа, микрофоны, монитор и громкость. Музыканты должны поддерживать церемонию и танцпол, оставаясь связанными с сетом DJ.',
        },
        {
          q: 'Подходят ли музыканты для всех видов хины?',
          a: 'Да, если инструмент подходит стилю семьи: марокканская, персидская, индийская, йеменская, тунисская, иракская, курдская или смешанная хина.',
        },
        {
          q: 'Когда лучше включать музыкантов?',
          a: 'Лучше всего они работают вокруг входа, церемониальных моментов, семейных кругов или короткого live-момента на танцполе.',
        },
      ];
    }

    return [
      {
        q: `Что самое важное при планировании ${title}?`,
        a: 'Важно сохранить семейную и традиционную идентичность и построить радостный танцпол для молодых и старших гостей. Правильный баланс уважает церемонию и сохраняет теплую атмосферу.',
      },
      {
        q: `Как совместить традицию и современную музыку в ${title}?`,
        a: 'Начните с песен и ритмов, которые знает семья, затем постепенно переходите к мизрахи, израильской музыке, актуальным хитам и танцевальной энергии.',
      },
      {
        q: `Какие детали согласовать заранее для ${title}?`,
        a: 'Согласуйте входы, наряды, нанесение хны, семейные моменты, тайминг блюд, развлечения и музыкальные точки. В хине много маленьких моментов, и порядок соединяет их в один вечер.',
      },
      {
        q: `Любой ли DJ подходит для ${title}?`,
        a: 'Не всегда. DJ для хины должен понимать традицию, семью и танцпол, знать когда дать место церемонии и когда открыть ритм.',
      },
    ];
  }

  return [
    {
      q: `Как понять, подходит ли ${title} моему событию?`,
      a: `Проверьте, подходит ли идея гостям, площадке, времени и вашему стилю. Хорошая отправная точка: ${firstTip}`,
    },
    {
      q: `Когда планировать ${title}?`,
      a: `Планируйте это при построении тайминга, а затем уточняйте, когда понятны гости, площадка и поставщики. Так ${title} остается частью общего потока.`,
    },
    {
      q: `Что согласовать с DJ по теме ${title}?`,
      a: 'Согласуйте тайминг, версии песен, объявления, технические потребности, сигналы для поставщиков и переход к следующему моменту.',
    },
    {
      q: `Какой ошибки избежать при планировании ${title}?`,
      a: `Не выбирайте только по тренду. Проверьте, подходит ли идея гостям и ходу вечера, а затем начните с этого: ${secondTip}`,
    },
  ];
}

function buildLocalizedArticleFaqs(
  args: LocalizedArticleFaqArgs,
): FAQItem[] | undefined {
  if (args.lang === 'en') return buildEnglishArticleFaqs(args);
  if (args.lang === 'fr') return buildFrenchArticleFaqs(args);
  if (args.lang === 'ru') return buildRussianArticleFaqs(args);

  return undefined;
}

function buildArticleFaqs(args: {
  key: PlanningArticleKey;
  title: string;
  serviceLabel: string;
  sectionTitle: string;
  bullets: string[];
  workBullets: string[];
  lang?: string;
}): FAQItem[] {
  const {
    key,
    title,
    serviceLabel,
    sectionTitle,
    bullets,
    workBullets,
    lang = 'he',
  } = args;
  const firstTip = bullets[0] ?? sectionTitle;
  const secondTip = bullets[1] ?? firstTip;
  const firstCheck =
    workBullets[0] ??
    'בודקים את סדר האירוע, הקהל, המוזיקה והספקים לפני שסוגרים תכנית.';
  const secondCheck = workBullets[1] ?? firstCheck;
  const thirdCheck = workBullets[2] ?? secondCheck;
  const eventLabel = getPlanningFaqEventLabel(key, lang, serviceLabel);
  const localizedFaqs = buildLocalizedArticleFaqs({
    key,
    title,
    serviceLabel,
    sectionTitle,
    bullets,
    workBullets,
    lang,
    eventLabel,
  });

  if (localizedFaqs) return localizedFaqs;

  const isWedding = key.startsWith('wedding_');
  const isHenna = key.startsWith('henna_');
  const isBarBat = key.startsWith('bar_bat_');

  const crowdText = isWedding
    ? 'זוג, משפחות, חברים צעירים ואורחים מבוגרים'
    : isHenna
      ? 'משפחה קרובה, עדות, צעירים ומבוגרים שרוצים גם טקס וגם שמחה'
      : isBarBat
        ? 'ילדים ונוער לצד הורים, סבים וסבתות ומשפחה מורחבת'
        : 'אורחים בגילים שונים, קהל עסקי או משפחתי ומטרת הערב';

  const planningAnswer = `כדאי לסגור את הכיוון כבר בשלב בניית הלו״ז, ואז לדייק אותו כשברור מי הקהל, מה סדר האירוע ומה קורה לפני ואחרי הרגע הזה. כך ${title} נכנס בזמן נכון בתוך הזרימה של ה${eventLabel}.`;

  const coordinationAnswer = `חשוב לתאם מראש עם התקליטן את ${firstCheck} בנוסף כדאי לבדוק את ${secondCheck} ואת ${thirdCheck} כדי שהמוזיקה, הסאונד, הצילום והספקים יעבדו יחד בתזמון ברור.`;

  if (key === 'wedding_chuppah_song') {
    return [
      {
        q: 'איך בוחרים שיר כניסה לחופה שמרגיש אישי?',
        a: 'מתחילים משיר שמרגיש אישי לזוג ולא רק משיר טרנדי. בודקים את המילים, הפתיחה, קצב ההליכה ונקודת השיא של השיר. לפעמים גרסה אקוסטית, קאבר איכותי או חיתוך מדויק נותנים תחושה הרבה יותר אישית משיר מוכר מדי.',
      },
      {
        q: 'כמה זמן צריך להיות שיר הכניסה לחופה?',
        a: 'האורך תלוי במרחק ההליכה, במספר המלווים ובסדר הכניסות. ברוב החופות לא צריך להשמיע שיר מלא, אלא להכין נקודת התחלה ונקודת יציאה מדויקות כדי שהרגע לא יימרח ולא יסתיים בפתאומיות.',
      },
      {
        q: 'האם כדאי לשלב נגנים או זיקוקים בכניסה לחופה?',
        a: 'אפשר, אבל רק אם זה משרת את הרגע ולא מכביד עליו. כנר, סקסופון, עשן או זיקוקים צריכים להיות מתואמים עם האולם, הצלמים והדי ג׳יי כדי שהאפקט יקרה בדיוק בשיא ולא יסתיר את הזוג.',
      },
      {
        q: 'מה צריך להעביר לתקליטן לפני החופה?',
        a: 'צריך להעביר את סדר הכניסות, השירים, גרסאות מדויקות, שמות המלווים, נקודת התחלה לכל שיר וכל אפקט שמתוכנן. ככל שהמידע ברור יותר מראש, החופה מרגישה רגועה, מכובדת ומדויקת יותר.',
      },
    ];
  }

  if (key === 'wedding_slow_song') {
    return [
      {
        q: 'איך בוחרים שיר סלואו שמרגיש אישי וטבעי?',
        a: 'בוחרים שיר שמתחבר לזוג באמת, גם אם הוא לא הבחירה הכי צפויה. כדאי לבדוק שהקצב נוח לריקוד, שהמילים מתאימות ושיש בשיר רגע שיא שאפשר לבנות סביבו את הריקוד.',
      },
      {
        q: 'מתי כדאי להזמין את המשפחה והחברים להצטרף לסלואו?',
        a: 'אם הזוג רוצה רגע אינטימי, משאירים את החצי הראשון רק לזוג ומכניסים משפחה וחברים בהדרגה. אם רוצים לפתוח רחבה מהר, אפשר לתכנן כניסה מוקדמת יותר של האורחים ולבנות מעבר לשיר שמח.',
      },
      {
        q: 'האם קונפטי, בלונים מתפוצצים, עשן או זיקוקים מתאימים לסלואו?',
        a: 'כן, אבל רק בתזמון נכון. זיקוקים, קונפטי, עשן כבד או בלונים מתפוצצים עובדים טוב כשהם נכנסים ברגע שיא בשיר, משאירים את הזוג במרכז ונראים טוב בצילום.',
      },
      {
        q: 'מה התקליטן צריך להכין לשיר סלואו?',
        a: 'צריך להכין את השיר הנבחר, להבין איפה מגיע רגע השיא, לתאם ווליום ומעבר לרחבה. אם יש אפקטים או עיצוב מיוחד, חשוב לתאם אותם מראש מול מפעיל האטרקציה, מנהל האירוע והצלמים.',
      },
    ];
  }

  if (key === 'bar_bat_recording') {
    return [
      {
        q: 'איזה שיר כדאי להקליט לבר או בת מצווה?',
        a: 'כדאי לבחור שיר שהחוגג או החוגגת מתחברים אליו ושאפשר להתאים לו מילים אישיות בצורה פשוטה וברורה. חשוב שהשיר יתאים לגיל, למשפחה ולאופי האירוע.',
      },
      {
        q: 'מתי מתחילים לעבוד על הקלטת שיר לבר/בת מצווה?',
        a: 'עדיף להתחיל כמה שבועות לפני האירוע, כדי שיהיה זמן לבחור שיר, לכתוב מילים, להקליט בנחת ולבדוק איך משלבים את השיר בכניסה, בקליפ או ברגע משפחתי.',
      },
      {
        q: 'האם חייבים קליפ לשיר מוקלט?',
        a: 'לא חייבים. לפעמים השיר עובד מצוין ככניסה או כרגע במה קצר. אם עושים קליפ, חשוב לשמור עליו ממוקד ולתכנן מראש איך עוברים ממנו להמשך האירוע.',
      },
      {
        q: 'מה חשוב לתאם עם הדי ג׳יי לגבי שיר מוקלט?',
        a: 'צריך להעביר קובץ איכותי, לבדוק ווליום, לוודא שיש גרסה סופית אחת, ולתכנן מה קורה מיד אחרי השיר: ברכה, כניסה, משחק או פתיחת רחבה.',
      },
    ];
  }

  if (key.endsWith('_music')) {
    return [
      {
        q: `איך בוחרים מוזיקה שמתאימה באמת ל${eventLabel}?`,
        a: `מתחילים ממיפוי הקהל: ${crowdText}. אחר כך בונים שילוב בין שירים שאתם אוהבים, סגנונות שמרימים את הרחבה ושירים שלא תרצו לשמוע. בחירה טובה לא מבוססת רק על פלייליסט, אלא על קריאת קהל בזמן אמת.`,
      },
      {
        q: 'איזה סגנונות מוזיקליים כדאי לשלב?',
        a: 'אפשר לשלב מזרחית, ישראלי, לטיני, היפ הופ, נוסטלגיות, מוזיקה דתית, טראנס, טכנו ולהיטים עדכניים. הסוד הוא לא להכניס הכל בכל מחיר, אלא לבנות מעבר נכון בין הסגנונות לפי הגיל, המשפחה והאנרגיה ברחבה.',
      },
      {
        q: 'כמה שירים כדאי לשלוח לתקליטן לפני האירוע?',
        a: 'כדאי לשלוח רשימת חובה קצרה, רשימת שירים אהובים ורשימת שירים שלא מתאימים לכם. רשימה ארוכה מדי יכולה לחסום את קריאת הקהל, ולכן עדיף לתת כיוון ברור ולהשאיר לדיג׳יי מקום להוביל את הרחבה.',
      },
      {
        q: `מה ההבדל בין מוזיקה טובה לבין תכנון מוזיקלי מקצועי ל${eventLabel}?`,
        a: 'מוזיקה טובה היא שירים שאוהבים. תכנון מקצועי הוא לדעת מתי להשמיע אותם, באיזה סדר, באיזה ווליום, איך לעבור בין קהלים וסגנונות, ואיך לשמור על אירוע שמרגיש שלכם.',
      },
    ];
  }

  if (key === 'henna_timeline') {
    return [
      {
        q: `איך בונים לו״ז נכון ל${eventLabel}?`,
        a: 'מתחילים מחלוקת הערב לשלבים: כניסה, החלפות לבוש, טקס מריחת חינה, רגעי משפחה, מנות וריקודים. לכל שלב צריך זמן ריאלי, מוזיקה מתאימה ומעבר ברור לשלב הבא.',
      },
      {
        q: 'איפה משלבים את המנות בלו״ז חינה?',
        a: 'את המנות מתאמים מול האולם לפי הכניסות והריקודים. חשוב להבין איפה האוכל יושב ביחס לטקס ולרחבה, כדי שהקהל יישאר מחובר למה שקורה באולם.',
      },
      {
        q: 'מי צריך לקבל את הלו״ז לפני החינה?',
        a: 'התקליטן, מנהל האירוע, האולם, הצלמים וכל ספק שאחראי על רגע מרכזי. כשכולם יודעים מתי הכניסה, הטקסים, המנות והריקודים, קל יותר לשמור על ערב מסודר ושמח.',
      },
      {
        q: 'איך יודעים אם הלו״ז צריך סידור מחדש?',
        a: 'אם הרבה כניסות, החלפות לבוש, טקסים ומנות יושבים ברצף, כדאי לסדר אותם מחדש. לו״ז טוב משאיר מקום למסורת, לאוכל ולריקודים בקצב שהקהל יכול לעקוב אחריו.',
      },
    ];
  }

  if (key.includes('timeline')) {
    return [
      {
        q: `איך בונים לו״ז נכון ל${eventLabel}?`,
        a: `מתחילים מחלוקת הערב לשלבים: קבלת פנים, כניסה או טקס, אוכל, ברכות ורחבה. לכל שלב צריך זמן ריאלי, מוזיקה מתאימה ומעבר ברור לשלב הבא.`,
      },
      {
        q: 'כמה מרווחים כדאי להשאיר בין חלקי האירוע?',
        a: 'כדאי להשאיר מרווחים קטנים לעיכובים טבעיים, ולשמור על סדר ברור שכל הספקים מבינים. הלו״ז צריך להיות גמיש מספיק לשטח וברור מספיק לביצוע.',
      },
      {
        q: 'מי צריך לקבל את הלו״ז לפני האירוע?',
        a: 'התקליטן, מנהל האירוע, האולם, הצלמים, המנחה וכל ספק שאחראי על אטרקציה או רגע שיא. כשכולם עובדים לפי אותו לו״ז, המעברים מרגישים טבעיים יותר.',
      },
      {
        q: 'איך יודעים אם הלו״ז צריך סידור מחדש?',
        a: 'אם הרבה כניסות, ברכות, קליפים ואטרקציות יושבים ברצף, כדאי לפזר אותם נכון יותר. לו״ז טוב משאיר מקום לאוכל, לשיחות ובעיקר לריקודים.',
      },
    ];
  }

  if (key === 'henna_attractions') {
    return [
      {
        q: 'אילו אטרקציות באמת מתאימות לחינה?',
        a: 'אטרקציות שמתאימות לחינה יכולות להיות קיאק פירות, רקדנית בטן, בר קוקטיילים, מגנטים, עמדות צילום, מזכרות, תלבושות לאורחים, אותיות מעוצבות או תאורה צבעונית.',
      },
      {
        q: 'איך בוחרים אטרקציות שמתחברות לטקס החינה?',
        a: 'בוחרים כמה אטרקציות שמתאימות באמת לאופי המשפחה ולאולם, ומשבצים אותן ברגעים נכונים. קיאק פירות, מגנטים, בר קוקטיילים או רקדנית בטן צריכים להשתלב בערב ולחזק את הטקס, הצילום והרחבה.',
      },
      {
        q: 'מתי משלבים מגנטים או עמדות צילום בחינה?',
        a: 'עמדות צילום ומגנטים עובדים טוב בקבלת הפנים, בין חלקי הערב וברגעים שבהם הקהל לא נמצא כולו ברחבה. כדאי לתזמן אותם כך שהם לא יתחרו בטקס או ברגע מוזיקלי מרכזי.',
      },
      {
        q: 'איך משלבים רקדנית בטן או קיאק פירות בחינה?',
        a: 'רקדנית בטן צריכה להיכנס כרגע קצר ומדויק עם מוזיקה מתאימה, וקיאק פירות עובד טוב כרגע שמח אחרי האוכל או בזמן שהרחבה פעילה. בשניהם חשוב לתאם מראש מול הדיג׳יי והאולם כדי שהאטרקציה תיכנס בצורה נקייה.',
      },
    ];
  }

  if (key.includes('attractions')) {
    return [
      {
        q: `אילו אטרקציות באמת מתאימות ל${eventLabel}?`,
        a: 'האטרקציות צריכות להתאים לקהל, לאולם ולאופי הערב. זיקוקים, תותחי עשן, בלונים מתפוצצים, לייזרים, אותיות מעוצבות, עמדות צילום ומגנטים יכולים לעבוד מצוין כשהם נכנסים בזמן נכון.',
      },
      {
        q: 'איך גורמים לאטרקציות לתמוך ברחבה?',
        a: 'בוחרים כל אטרקציה לפי רגע אמיתי באירוע. בודקים איפה היא עומדת, כמה זמן היא לוקחת, האם היא משפיעה על צילום או ריקודים, ומה המוזיקה שצריכה ללוות אותה.',
      },
      {
        q: 'מתי כדאי להפעיל זיקוקים, עשן, לייזרים או בלונים מתפוצצים?',
        a: 'הזמן הנכון הוא רגע שיא ברור: כניסה, סלואו, פתיחת רחבה, דרופ מוזיקלי או אפטר פארטי. כשהתזמון מדויק, האפקט מרגיש טבעי ומרגש.',
      },
      {
        q: 'מה חייבים לבדוק מול האולם לפני שסוגרים אטרקציה?',
        a: 'צריך לבדוק אישורים, בטיחות, חשמל, גובה תקרה, מיקום, זמן הקמה והשפעה על צילום. חלק מהאולמות מגבילים עשן, זיקוקים או ציוד מסוים, ולכן סוגרים רק אחרי אישור ברור.',
      },
    ];
  }

  if (key.includes('musicians')) {
    const musicianExamplesByKey: Partial<Record<PlanningArticleKey, string>> = {
      wedding_musicians:
        'בחתונה בוחרים נגן לפי הרגע: כינור, גיטרה או סקסופון יכולים להתאים לקבלת פנים רגועה, נגן בחופה צריך להיות מתואם לשיר ולקצב ההליכה, ובוזוקי, דרבוקות, מתופפים או סקסופון ברחבה צריכים להיכנס בקטע קצר וברור.',
      henna_musicians:
        'דרבוקיסט לחינה מתאים לכניסה ולמעגלים, כנר לחינה יכול להוסיף רגע חגיגי, וסקסופון, בוזוקי, גיטרה, דרבוקות, מתופפים, קלידים, זמרים או להקה מתאימים כשיש להם מקום ברור בלו״ז.',
      bar_bat_musicians:
        'סקסופון לבר מצווה מתאים לפתיחת רחבה, כנר לבר מצווה יכול לחזק רגע משפחתי, מתופפים או דרבוקות עובדים טוב בכניסה קצרה, וגיטרה, קלידים, זמרים או להקה צריכים להיות קצרים ומותאמים לנוער.',
      event_musicians:
        'כנר לאירוע מתאים לאווירה אלגנטית, סקסופון לאירוע מתאים לקבלת פנים או רחבה, בוזוקי וגיטרה נותנים צבע ים תיכוני, ודרבוקות, מתופפים, זמרים, קלידים או להקה לאירוע מתאימים רק כשיש להם מקום ברור בלו״ז.',
    };
    const musicianExamples =
      musicianExamplesByKey[key as PlanningArticleKey] ??
      'סקסופון מתאים לקבלת פנים או רחבה, כנר יכול לחזק רגע מרגש, דרבוקות ומתופפים מתאימים לפתיחה שמחה, והרכב חי מתאים לאווירה אלגנטית.';

    return [
      {
        q: `מתי כדאי לשלב נגנים ב${eventLabel}?`,
        a: 'נגנים עובדים הכי טוב כשהם מחזקים רגע ברור: קבלת פנים, כניסה, טקס, פתיחת רחבה או קטע שיא קצר. תפקיד ברור עוזר להם להשתלב נכון עם הדיג׳יי.',
      },
      {
        q: 'איזה נגן מתאים לאיזה סוג רגע?',
        a: `${musicianExamples} הבחירה תלויה בקהל, במקום, בסאונד ובאופי האירוע.`,
      },
      {
        q: 'מה צריך לתאם בין הנגנים לתקליטן?',
        a: 'צריך לתאם שירים, סולמות, נקודות כניסה, אורך הופעה, מיקרופונים, DI, מוניטור, קלידים אם יש, חיבורי סאונד ומעברים. נגן טוב נשמע הכי טוב כשהוא יודע בדיוק מתי להצטרף ואיך להתחבר לסט של הדיג׳יי.',
      },
      {
        q: 'האם נגנים מתאימים גם לאירוע עם קהל צעיר?',
        a: 'כן, אם שומרים על קטע קצר ומדויק. באירוע צעיר הנגן צריך להוסיף רגע חי שמתחבר מהר בחזרה למסיבה.',
      },
    ];
  }

  if (key === 'henna_entrance') {
    return [
      {
        q: `איך מתכננים כניסה מרשימה ל${eventLabel}?`,
        a: `כניסה לחינה מתחילה בסדר ברור: שיר כניסה לחינה, משפחה ומלווים. מחליטים מראש אם הארוס והארוסה נכנסים יחד או בשלבים, מי נכנס לפניהם ומה קורה מיד אחרי הכניסה.`,
      },
      {
        q: 'איך בוחרים שיר כניסה לחינה?',
        a: 'בוחרים שיר שמתאים לעדה, לאופי של המשפחה ולקצב הכניסה. חשוב לבדוק מראש איפה השיר מתחיל, איפה הוא מגיע לשיא ומתי עוברים לטקס מריחת החינה.',
      },
      {
        q: 'כמה זמן כניסה לחינה צריכה להימשך?',
        a: 'כניסה לחינה צריכה לתת מקום לארוס, לארוסה, להורים ולמלווים. בדרך כלל מתאימים את אורך השיר למרחק ההליכה ולמספר הנכנסים.',
      },
      {
        q: 'מה חשוב להעביר לתקליטן לפני הכניסה לחינה?',
        a: 'חשוב להעביר את שיר הכניסה ואת סדר הנכנסים. בנוסף מעדכנים מה מגיע מיד אחרי הכניסה: טקס מריחת החינה, רגע משפחתי, החלפת לבוש או ריקודים.',
      },
    ];
  }

  if (key.includes('entrance')) {
    return [
      {
        q: `איך מתכננים כניסה מרשימה ל${eventLabel}?`,
        a: `כניסה טובה מתחילה בשיר נכון, סדר ברור ותזמון מדויק. צריך לדעת מי נכנס, מאיפה נכנסים, כמה זמן ההליכה לוקחת ומה קורה מיד אחרי הכניסה, כדי שהרגע יפתח את ה${eventLabel} באנרגיה נכונה.`,
      },
      {
        q: 'האם כדאי להוסיף תופים, עשן או זיקוקים לכניסה?',
        a: 'אפשר, אבל רק אם זה מתאים למקום ולקהל. תופים, עשן או זיקוקים צריכים להיכנס על נקודת שיא במוזיקה, לא להסתיר את המצולמים ולא ליצור עיכוב טכני.',
      },
      {
        q: 'כמה זמן כניסה צריכה להימשך?',
        a: 'כניסה צריכה להיות קצרה, ברורה ומדויקת. כשהיא ארוכה מדי הקהל מאבד ריכוז, וכשהיא קצרה מדי הרגע לא מקבל מקום. לכן מתאימים את אורך השיר למרחק ההליכה ולמספר הנכנסים.',
      },
      {
        q: 'מה חשוב להעביר לתקליטן לפני הכניסה?',
        a: 'צריך להעביר את סדר הנכנסים, השיר, נקודת ההתחלה, הכרזה אם יש, ואטרקציות שמתוכננות. ככל שהתיאום ברור יותר, הכניסה נראית טבעית ומקצועית יותר.',
      },
    ];
  }

  if (isHennaTraditionArticle(key)) {
    return [
      {
        q: `מה הכי חשוב לשמור בתכנון ${title}?`,
        a: `חשוב לשמור על הזהות המשפחתית והמסורתית של ה${title}, ולבנות לצידה רחבה שמחה לצעירים ולמבוגרים. השילוב הנכון הוא מוזיקה שמכבדת את הבית והעדה יחד עם קצב שמחבר את כולם.`,
      },
      {
        q: `איך משלבים בין מסורת לבין מוזיקה עדכנית ב${title}?`,
        a: 'מתחילים בשירים ובמקצבים שהמשפחה מכירה, ואז מחברים אותם בהדרגה למזרחית, ישראלי, להיטים עדכניים ומקצבי רחבה. מעבר חד מדי יכול להרגיש לא טבעי, ולכן חשוב לבנות את האנרגיה בשלבים.',
      },
      {
        q: `אילו פרטים צריך לתאם מראש ב${title}?`,
        a: 'צריך לתאם כניסות, לבוש, טקס מריחת חינה, רגעי משפחה, אטרקציות ונקודות מוזיקליות. בחינה יש הרבה רגעים קטנים, ותכנון מוקדם מחבר אותם לערב אחד ברור ושמח.',
      },
      {
        q: `האם כל תקליטן מתאים ל${title}?`,
        a: 'לא תמיד. דיג׳יי לחינה צריך להבין גם מסורת, גם משפחה וגם רחבה. הוא צריך לדעת מתי לתת כבוד לטקס ומתי לפתוח קצב, כך שהחינה תישאר חגיגית, משפחתית ושמחה.',
      },
    ];
  }

  return [
    {
      q: `איך יודעים אם ${title} מתאים לאירוע שלי?`,
      a: `בודקים אם הרעיון מתאים לקהל, למקום, לשעה שבה הוא קורה ולאופי שלכם. נקודת התחלה טובה היא: ${firstTip} אם זה מרגיש טבעי בתוך הלו״ז ומחזק את הרגע, זה סימן טוב.`,
    },
    {
      q: `מתי כדאי לתכנן את ${title}?`,
      a: planningAnswer,
    },
    {
      q: `מה חשוב לתאם עם התקליטן לגבי ${title}?`,
      a: coordinationAnswer,
    },
    {
      q: `איזו טעות כדאי להימנע ממנה כשמתכננים ${title}?`,
      a: `הטעות הנפוצה היא לבחור לפי טרנד בלי לבדוק אם זה מתאים לאנשים שמגיעים ולרצף של הערב. כדאי להתחיל מהדבר הבא: ${secondTip} ומשם לבנות תכנון שמרגיש אישי, ברור ומכבד את ה${eventLabel}.`,
    },
  ];
}

type PlanningItemOptions = {
  description?: string;
  seoContext?: string;
};

function buildPlanningTargetSeoContext(
  article: PlanningArticle,
  service: SeoService,
  i18n: I18nConstants,
  city?: SeoCity | string,
) {
  if (i18n.HTML_LANG !== 'he') return '';

  const key = String(article.key);
  const cityLabel = city ? i18n.translateCity(String(city)) : '';
  const cityBet = withBet(cityLabel);
  const primaryKeyword = `${i18n.preferredKeywordForService(service)}${cityBet}`;

  if (key.startsWith('city_venue_guide_')) {
    return trimPlanningSeoText(
      `בחירת אולם${cityBet || ' לאירוע'} משפיעה ישירות על העבודה של ${primaryKeyword}: גודל הרחבה, מיקום עמדת הדיג׳יי, אקוסטיקה, הגברה, תפעול ספקים ומגבלות סאונד יקבעו איך המוזיקה תישמע ואיך האורחים יזוזו בין קבלת פנים, טקס וריקודים.`,
      340,
    );
  }

  const copyByKey: Record<string, string> = {
    wedding_chuppah_song: `זוגות שמחפשים ${primaryKeyword} בודקים כמעט תמיד גם שיר כניסה לחופה, שיר שבירת כוס וסדר מלווים. הבחירה הנכונה צריכה להתאים לקצב ההליכה, למיקום החופה, לווליום במקום ולמעבר הראשון אל הרחבה.`,
    wedding_timeline: `כשסוגרים ${primaryKeyword}, הלו״ז הוא אחד הדברים שמבדילים בין חתונה זורמת לבין ערב עם המתנות ארוכות. קבלת פנים, חופה, כניסות לרחבת הריקודים, סלואו, הפסקות אוכל ואפטר פארטי צריכים לקבל תזמון ברור.`,
    wedding_attractions: `לפני שסוגרים זיקוקים, עשן כבד, בלונים מתפוצצים, לייזרים, אותיות מעוצבות, עמדות צילום או מגנטים, כדאי לתאם עם ${primaryKeyword} את רגע ההפעלה, המוזיקה, התאורה וההשפעה על הצילום ורחבת הריקודים.`,
    wedding_musicians: `נגנים לחתונה צריכים להיכנס לתפקיד ברור: קבלת פנים חיה, חופה מרגשת, סלואו או קטע רחבה קצר. עם ${primaryKeyword} חשוב לתאם מראש שירים, סולמות, חיבורי סאונד, נקודות כניסה ומעבר חזרה לסט של הדיג׳יי.`,
    wedding_slow_song: `ברגע הסלואו כדאי לערב את ${primaryKeyword} כבר בבחירת השיר. בודקים התאמה לריקוד, רגע שיא, הצטרפות משפחה ואפקטים כמו זיקוקים, קונפטי, עשן כבד ובלונים מתפוצצים כדי שהמעבר לרחבה ירגיש טבעי.`,
    wedding_music: `כאן נכנס התפקיד של ${primaryKeyword}: לקרוא משפחה, חברים, צעירים, מבוגרים וקהל דתי, ולשלב מזרחית, ישראלי, לטיני, היפ הופ, נוסטלגיות וטראנס תוך שמירה על הטעם של הזוג.`,

    henna_entrance: `בכניסה לחינה ${primaryKeyword} צריך לתזמן את שיר הכניסה, סדר הארוס והארוסה, ההורים והמלווים, ואת המעבר לטקס מריחת החינה, לרגע משפחתי או לריקודים.`,
    henna_timeline: `עם ${primaryKeyword} מסדרים מראש כניסה, החלפות לבוש, טקס מריחת חינה, רגעי משפחה, מנות בין הכניסות והריקודים, מעגלים ורחבה. המטרה היא לשמור על מסורת, משפחה וקצב שמח.`,
    henna_attractions: `כשמתכננים חינה עם ${primaryKeyword}, אפשר לשלב קיאק פירות, רקדנית בטן, בר קוקטיילים, תלבושות לאורחים, עמדות צילום, מגנטים, מזכרות מיוחדות או אותיות מעוצבות. כל אטרקציה צריכה להתאים למשפחה, לאולם ולרצף של הרחבה.`,
    henna_musicians: `דרבוקיסט לחינה, כנר לחינה, סקסופון, בוזוקי, גיטרה, דרבוקות, מתופפים, קלידים, זמרים או להקה צריכים להיכנס בתזמון נכון. ${primaryKeyword} מסנכרן מקצבים, ווליום ומעברים בין כניסה, טקס חינה ורחבת ריקודים.`,
    henna_music: `אחרי שמזהים את העדה והשירים החשובים למשפחה, ${primaryKeyword} בונה מעבר טבעי מהטקס לרחבה: קודם מסורת, אחר כך מזרחית, ישראלי ולהיטים שהצעירים אוהבים.`,
    henna_moroccan: `בטקס חינה מרוקאית חשוב לתזמן כניסות, לבוש מסורתי, טקס מריחת חינה, רגעי משפחה ושירי חינה מרוקאית. ${primaryKeyword} שומר על צבע ומסורת, ואז פותח מעבר טבעי לרחבה.`,
    henna_kurdish: `חינה כורדית מסורתית צריכה לתת מקום לשירים כורדיים, מעגלים וקצב שמוכר למבוגרים, ועדיין לפתוח רחבה לצעירים. ${primaryKeyword} עוזר לאזן בין מסורת כורדית, מזרחית ולהיטים מוכרים.`,
    henna_tunisian: `חינה תוניסאית מסורתית דורשת שירי כניסה, טקס מריחת חינה, רגעי משפחה ומעגלים. ${primaryKeyword} מחבר בין מקצבים תוניסאיים, שירים משפחתיים ומוזיקה שפותחת רחבה שמחה.`,
    henna_yemenite: `חינה תימנית מסורתית נותנת מקום לשירה, לבוש, טקס ושירים תימניים שמחברים בין הדורות. עם ${primaryKeyword} מתכננים מתי נשארים במסורת ומתי עוברים למזרחית, ישראלי וקצב שמתאים לצעירים.`,
    henna_indian: `בטקס חינה הודית צריך לתאם כניסות, לבוש, טקס מריחת חינה, רגעי משפחה ושירי חינה הודית. ${primaryKeyword} משלב מוזיקה הודית לחינה עם מזרחית ולהיטים ישראליים ברחבה אחת.`,
    henna_iraqi: `חינה עיראקית מסורתית נשענת על שירים עיראקיים שמכירים מהבית, טקס מריחת חינה ומעגלים משפחתיים. ${primaryKeyword} משלב מוזיקה עיראקית, מזרחית ולהיטים לצעירים תוך שמירה על האופי של הטקס.`,
    henna_persian: `בטקס חינה פרסית שומרים על אלגנטיות, משפחתיות ושירים פרסיים לחינה, ובונים מעבר הדרגתי לרחבה שמחה ועדכנית עם ${primaryKeyword}.`,

    bar_bat_recording: `כשמשלבים שיר מוקלט בכניסה, בקליפ או ברגע משפחתי, ${primaryKeyword} צריך לקבל קובץ איכותי, נקודת השמעה ברורה ומעבר נכון להמשך המסיבה.`,
    bar_bat_entrance: `עם ${primaryKeyword} חשוב לתכנן שיר כניסה, הכרזה, חברים, משפחה, רקדנים או אפקטים באורך קצר ומדויק. הכניסה צריכה לתת לחוגג ביטחון ורגע שמח מול החברים.`,
    bar_bat_timeline: `עם ${primaryKeyword} מסדרים כניסה, דרשה, קליפ, אוכל, משחקי רחבה וריקודים כך שהילדים יישארו בעניינים והמבוגרים ירגישו חלק. לו״ז טוב מתאים גם לנוער וגם למשפחה.`,
    bar_bat_attractions: `לפני שסוגרים עמדות צילום, מגנטים, בלונים מתפוצצים, אותיות מעוצבות, לייזרים, עשן או נגנים, כדאי לתאם עם ${primaryKeyword} את המיקום, התזמון והמוזיקה. אטרקציה טובה מחזקת את החוויה ומחזירה את הנוער לרחבה.`,
    bar_bat_musicians: `סקסופון לבר מצווה, כנר לבר מצווה, מתופפים, דרבוקות, גיטרה, קלידים, זמרים או להקה קצרה יכולים להשתלב עם ${primaryKeyword} בכניסה, בקבלת פנים או בפתיחת הרחבה. באירוע נוער הקטע צריך להיות קצר, חי וברור.`,
    bar_bat_music: `האתגר של ${primaryKeyword} הוא לדבר גם לחברים מהכיתה וגם למשפחה. שילוב נכון של להיטי נוער, פופ, היפ הופ, מזרחית, נוסטלגיות ושירים שההורים אוהבים מחזיק רחבה מגוונת.`,

    event_entrance: `עם ${primaryKeyword} מתכננים שיר פתיחה, הכרזה, תאורה, מיקרופון ומעבר ברור מקבלת הפנים להמשך הערב. כניסה מדויקת נותנת לכל אירוע התחלה מסודרת, חגיגית ומקצועית.`,
    event_music: `הדבר הראשון ש${primaryKeyword} צריך להבין הוא מי הקהל ומה מטרת הערב. משם בונים מעבר בין מוזיקת רקע, קבלת פנים, ברכות, מסיבה, מזרחית, ישראלי, לטיני, היפ הופ ונוסטלגיות.`,
    event_music_timeline: `עם ${primaryKeyword} מסדרים קבלת פנים, פתיחה, ברכות, מצגת, אוכל וריקודים כך שהמוזיקה תלווה את הערב ולא תגיע באיחור לרגעי השיא. זה חשוב במיוחד באירועי חברה והשקות.`,
    event_attractions: `באירוע חברה, אירוע פרטי, השקה או מסיבה, זיקוקים, עשן, לייזרים, עמדות צילום, מגנטים, בלונים מתפוצצים, אותיות מעוצבות או אפטר פארטי צריכים תיאום עם ${primaryKeyword}. כך האטרקציות משרתות את מטרת הערב.`,
    event_musicians: `עם ${primaryKeyword} בודקים רפרטואר, חיבורים, ווליום ונקודות כניסה של כנר לאירוע, סקסופון לאירוע, בוזוקי, גיטרה, דרבוקות, מתופפים, זמרים, קלידים או להקה לאירוע. קטע חי צריך לחזק את האווירה ולהתחבר למוזיקה.`,
  };

  return trimPlanningSeoText(
    copyByKey[key] ??
      `מי שמחפש ${primaryKeyword} צריך לחשוב על המדריך הזה כחלק מהתכנון המוזיקלי: קהל, אולם, סאונד, לו״ז, רגעי שיא ורחבת ריקודים צריכים לעבוד יחד כדי שהאירוע ירגיש טבעי ושמח.`,
    340,
  );
}

function planningItemFromArticle(
  article: PlanningArticle,
  options?: string | PlanningItemOptions,
): PlanningItem {
  const description =
    typeof options === 'string' ? options : options?.description;
  const seoContext =
    typeof options === 'string' ? undefined : options?.seoContext;

  return {
    title: article.title,
    description: buildPlanningCardDescription(article, description),
    ...(seoContext ? { seoContext: trimPlanningSeoText(seoContext, 300) } : {}),
    seoSummary: trimPlanningSeoText(
      [article.intro, article.cta].filter(Boolean).join(' '),
    ),
    seoBullets: buildPlanningItemSeoBullets(article),
    href: articlePath(article.slug),
    image: article.image,
    imageSrcSet: article.imageSrcSet,
    imageSizes: PLANNING_CARD_IMAGE_SIZES,
    imageAlt: article.imageAlt,
    ...(article.locationCity ? { locationCity: article.locationCity } : {}),
  };
}

function buildRelatedArticles(
  meta: PlanningArticleMeta,
  i18n: I18nConstants,
): PlanningItem[] {
  const keys = getSectionKeysForService(meta.service, i18n).filter(
    key => key !== meta.key,
  );

  return keys
    .map(key => metaByKey.get(key))
    .filter((item): item is PlanningArticleMeta => Boolean(item))
    .map(item => {
      const article = articleFromMeta(item, i18n, { includeRelated: false });

      return planningItemFromArticle(article, {
        seoContext: buildPlanningTargetSeoContext(article, meta.service, i18n),
      });
    });
}

function articleFromMeta(
  meta: PlanningArticleMeta,
  i18n: I18nConstants,
  options: { includeRelated?: boolean } = {},
): PlanningArticle {
  const copy = i18n.PLANNING_ARTICLES[meta.key];
  const parentServiceLabel = i18n.preferredKeywordForService(meta.service);
  const baseTrustParagraphs =
    copy.trustParagraphs ??
    i18n.PLANNING_TRUST_PARAGRAPHS(
      copy.title,
      parentServiceLabel,
      i18n.BRAND_TITLE_SHORT,
    );
  const trustParagraphs = baseTrustParagraphs;
  const workTitle = copy.workTitle ?? i18n.PLANNING_WORK_TITLE;
  const workBullets = copy.workBullets ?? i18n.PLANNING_WORK_BULLETS;
  const faqs: FAQItem[] =
    copy.faqs ??
    buildArticleFaqs({
      key: meta.key,
      title: copy.title,
      serviceLabel: parentServiceLabel,
      sectionTitle: copy.sectionTitle,
      bullets: copy.bullets,
      workBullets,
      lang: i18n.HTML_LANG,
    });

  return {
    key: meta.key,
    slug: meta.slug,
    service: meta.service,
    image: meta.image,
    imageSrcSet: planningImageSrcSet(meta.key),
    imageSizes: PLANNING_ARTICLE_IMAGE_SIZES,
    title: copy.title,
    seoTitle: buildPlanningSeoTitle(
      meta.key,
      copy.title,
      meta.service,
      i18n.HTML_LANG,
    ),
    sectionTitle: copy.sectionTitle,
    description: copy.description,
    seoDescription: buildPlanningSeoDescription({
      key: meta.key,
      title: copy.title,
      fallback: copy.description,
      lang: i18n.HTML_LANG,
    }),
    carouselDescription: (copy as { carouselDescription?: string })
      .carouselDescription,
    imageAlt: copy.imageAlt,
    intro: copy.intro,
    bullets: copy.bullets,
    cta: copy.cta,
    parentEventType: typeByService[meta.service],
    serviceDisplayLabel: getPlanningServiceDisplayLabel(meta.service, i18n),
    parentServiceLabel,
    parentCtaLabel: buildPlanningParentCtaLabel(
      meta.service,
      parentServiceLabel,
    ),
    parentLinkLabel: i18n.PLANNING_PARENT_LINK_LABEL(parentServiceLabel),
    trustTitle: i18n.PLANNING_TRUST_TITLE,
    trustParagraphs,
    workTitle,
    workBullets,
    faqs,
    relatedArticles:
      options.includeRelated === false ? [] : buildRelatedArticles(meta, i18n),
    articleSection: parentServiceLabel,
    keywords:
      copy.keywords ??
      buildArticleKeywords(copy.title, parentServiceLabel, meta.service),
    wordCount: estimateWordCount([
      copy.title,
      copy.description,
      copy.intro,
      copy.bullets,
      trustParagraphs,
      workTitle,
      workBullets,
      faqs.flatMap(faq => [faq.q, faq.a]),
    ]),
  };
}

function stripLocalizedCountryScope(label: string, lang: string) {
  const cleanLabel = String(label ?? '')
    .replace(/\s+/g, ' ')
    .trim();

  if (lang === 'en') return cleanLabel.replace(/\s+in Israel$/i, '');
  if (lang === 'fr') return cleanLabel.replace(/\s+en Israël$/i, '');
  if (lang === 'ru') return cleanLabel.replace(/\s+в Израиле$/iu, '');

  return cleanLabel;
}

function buildCityServiceLabel(
  service: SeoService,
  cityName: Exclude<SeoCity, ''>,
  cityLabel: string,
  i18n: I18nConstants,
) {
  const serviceLabel = i18n.preferredKeywordForService(service);

  if (i18n.HTML_LANG === 'he') return `${serviceLabel}${withBet(cityName)}`;

  const scopedLabel = stripLocalizedCountryScope(serviceLabel, i18n.HTML_LANG);
  if (i18n.HTML_LANG === 'fr') return `${scopedLabel} à ${cityLabel}`;
  if (i18n.HTML_LANG === 'ru') return `${scopedLabel} в ${cityLabel}`;

  return `${scopedLabel} in ${cityLabel}`;
}

function getRepresentativeMetaForService(
  service: SeoService,
  i18n: I18nConstants,
) {
  const key = getSectionKeysForService(service, i18n)[0];
  return key ? metaByKey.get(key) : undefined;
}

function buildCityVenueRelatedArticles(
  cityName: Exclude<SeoCity, ''>,
  i18n: I18nConstants,
): PlanningItem[] {
  const cityLabel = i18n.translateCity(cityName);
  const cityBet = i18n.HTML_LANG === 'he' ? withBet(cityName) : '';
  const copy = i18n.PLANNING_CITY_VENUE_GUIDE;

  return EVENT_TYPES.map(type => {
    const service = typeByService[type];
    const title = buildCityServiceLabel(service, cityName, cityLabel, i18n);
    const description =
      typeof copy.relatedServiceDescription === 'function'
        ? copy.relatedServiceDescription(title, cityLabel, cityBet)
        : `${title} - ${copy.description(cityLabel, cityBet)}`;
    const meta = getRepresentativeMetaForService(service, i18n);
    const imageKey = meta?.key ?? CITY_VENUE_GUIDE_IMAGE_KEY;

    return {
      title,
      description,
      seoContext: description,
      seoSummary: description,
      href: typeLocationPath(type, cityName),
      image: meta?.image ?? CITY_VENUE_GUIDE_IMAGE,
      imageSrcSet: planningImageSrcSet(imageKey),
      imageSizes: PLANNING_CARD_IMAGE_SIZES,
      imageAlt: `${title} - ${i18n.BRAND_TITLE_SHORT}`,
    };
  });
}

export function buildCityVenueGuideArticle(
  city: SeoCity | string,
  i18n: I18nConstants,
  options: { includeRelated?: boolean } = {},
): PlanningArticle | undefined {
  const data = getVenueGuideData(city, i18n);
  if (!data) return undefined;

  const { city: cityName, venues } = data;
  const cityLabel = i18n.translateCity(cityName);
  const cityBet = i18n.HTML_LANG === 'he' ? withBet(cityName) : '';
  const copy = i18n.PLANNING_CITY_VENUE_GUIDE;
  const title = copy.title(cityLabel, cityBet);
  const venuePreview = previewVenueNames(venues, 8, copy.moreLabel);
  const parentServiceLabel = copy.parentServiceLabel(
    i18n.preferredKeywordForService(SEO_SERVICE_EVENT),
    cityLabel,
    cityBet,
  );
  const bullets = [
    ...copy.overviewBullets(cityLabel, cityBet),
    copy.venueListBullet(cityLabel, cityBet, venues),
  ];
  const trustParagraphs = copy.trustParagraphs(
    cityLabel,
    cityBet,
    venuePreview,
  );
  const workBullets = copy.workBullets(cityLabel, cityBet);
  const faqs: FAQItem[] = copy.faqs(cityLabel, cityBet, venues);

  return {
    key: `city_venue_guide_${cityName}`,
    slug: getCityVenueGuideSlug(cityName),
    service: SEO_SERVICE_EVENT,
    image: CITY_VENUE_GUIDE_IMAGE,
    imageSrcSet: planningImageSrcSet(CITY_VENUE_GUIDE_IMAGE_KEY),
    imageSizes: PLANNING_ARTICLE_IMAGE_SIZES,
    title,
    seoTitle: buildPlanningSeoTitle(
      `city_venue_guide_${cityName}`,
      title,
      SEO_SERVICE_EVENT,
      i18n.HTML_LANG,
    ),
    sectionTitle: copy.sectionTitle(cityLabel, cityBet),
    description: copy.description(cityLabel, cityBet),
    seoDescription: buildPlanningSeoDescription({
      key: `city_venue_guide_${cityName}`,
      title,
      fallback: copy.description(cityLabel, cityBet),
      lang: i18n.HTML_LANG,
      venueNames: venues,
    }),
    imageAlt: `${title} - ${i18n.BRAND_TITLE_SHORT}`,
    intro: copy.intro(cityLabel, cityBet, venues.length, venuePreview),
    bullets,
    cta: copy.cta(cityLabel, cityBet),
    parentEventType: 'אירוע',
    parentHref: typeLocationPath('אירוע', cityName),
    locationCity: cityName,
    serviceDisplayLabel: copy.groupTitle,
    parentServiceLabel,
    parentCtaLabel: parentServiceLabel,
    parentLinkLabel: i18n.PLANNING_PARENT_LINK_LABEL(parentServiceLabel),
    trustTitle: copy.trustTitle(cityLabel, cityBet),
    trustParagraphs,
    workTitle: copy.workTitle(cityLabel, cityBet),
    workBullets,
    faqs,
    relatedArticles:
      options.includeRelated === false
        ? []
        : buildCityVenueRelatedArticles(cityName, i18n),
    articleSection: copy.articleSection(cityLabel, cityBet),
    keywords: [
      title,
      ...copy.keywords(cityLabel, cityBet),
      parentServiceLabel,
      i18n.BRAND_TITLE_SHORT,
    ],
    wordCount: estimateWordCount([
      title,
      `אולמות אירועים${cityBet}`,
      bullets,
      trustParagraphs,
      workBullets,
      faqs.flatMap(faq => [faq.q, faq.a]),
    ]),
  };
}

export function getCityVenueGuideArticles(i18n: I18nConstants) {
  return SEO_ALL_CITIES.map(city =>
    buildCityVenueGuideArticle(city, i18n),
  ).filter((article): article is PlanningArticle => Boolean(article));
}

export function getCityVenueGuideArticleBySlug(
  slug: string | undefined,
  i18n: I18nConstants,
) {
  const cleanSlug = normalizePlanningSlug(slug);

  return getCityVenueGuideArticles(i18n).find(article => {
    if (article.slug === cleanSlug) return true;
    if (!article.locationCity) return false;

    return getLegacyCityVenueGuideSlug(article.locationCity) === cleanSlug;
  });
}

export function buildCityVenueGuidePlanningItem(
  city: SeoCity | string,
  i18n: I18nConstants,
  targetService: SeoService = SEO_SERVICE_EVENT,
) {
  const article = buildCityVenueGuideArticle(city, i18n, {
    includeRelated: false,
  });
  if (!article) return undefined;

  const data = getVenueGuideData(city, i18n);
  const copy = i18n.PLANNING_CITY_VENUE_GUIDE;
  const cityLabel = data ? i18n.translateCity(data.city) : '';
  const cityBet = data && i18n.HTML_LANG === 'he' ? withBet(data.city) : '';
  const venuePreview = data ? data.venues.join(', ') : '';

  return planningItemFromArticle(article, {
    description: copy.planningItemDescription(cityLabel, cityBet, venuePreview),
    seoContext: buildPlanningTargetSeoContext(
      article,
      targetService,
      i18n,
      city,
    ),
  });
}

export function getPlanningArticles(i18n: I18nConstants) {
  return [
    ...EVENT_PLANNING_ARTICLE_META.map(meta => articleFromMeta(meta, i18n)),
    ...getCityVenueGuideArticles(i18n),
  ];
}

export function getPlanningArticleBySlug(
  slug: string | undefined,
  i18n: I18nConstants,
) {
  const cleanSlug = normalizePlanningSlug(slug);
  const meta = metaBySlug.get(cleanSlug);
  return meta
    ? articleFromMeta(meta, i18n)
    : getCityVenueGuideArticleBySlug(cleanSlug, i18n);
}

export function getPlanningArticlePath(slug: string) {
  return articlePath(slug);
}

export function getPlanningArticleUrl(slug: string) {
  return `${BASE_URL}${articlePath(slug)}`;
}

export function buildPlanningSectionForService(
  service: SeoService,
  i18n: I18nConstants,
  options: { city?: SeoCity | string } = {},
) {
  const sectionCopy =
    i18n.PLANNING_SECTIONS[service] ??
    i18n.PLANNING_SECTIONS[SEO_SERVICE_EVENT];

  const keys = sectionCopy.keys as PlanningArticleKey[];

  const baseItems: PlanningItem[] = keys
    .map(key => metaByKey.get(key))
    .filter((meta): meta is PlanningArticleMeta => Boolean(meta))
    .map(meta => {
      const article = articleFromMeta(meta, i18n);

      return planningItemFromArticle(article, {
        seoContext: buildPlanningTargetSeoContext(
          article,
          service,
          i18n,
          options.city,
        ),
      });
    });
  const cityVenueGuideItem = options.city
    ? buildCityVenueGuidePlanningItem(options.city, i18n, service)
    : undefined;
  const items = cityVenueGuideItem
    ? [cityVenueGuideItem, ...baseItems]
    : baseItems;

  return {
    title: sectionCopy.title,
    subtitle: sectionCopy.subtitle,
    readMoreLabel: i18n.PLANNING_READ_MORE_LABEL,
    previousLabel: i18n.PLANNING_PREVIOUS_LABEL,
    nextLabel: i18n.PLANNING_NEXT_LABEL,
    carouselLabel: i18n.PLANNING_CAROUSEL_LABEL(sectionCopy.title),
    items,
  };
}

const CITY_VENUE_GUIDE_REGION_ORDER: readonly Region[] = [
  'דרום',
  'מרכז',
  'צפון',
];

const buildCityVenueRegionCopy = (region: Region, i18n: I18nConstants) => {
  const regionLabel = i18n.translateRegion(region);

  if (i18n.HTML_LANG === 'he') {
    const regionBet = withBet(region);

    return {
      title: `בחירת אולם${regionBet}`,
      subtitle: `כל מדריכי בחירת האולם${regionBet} לפי עיר: רשימת אולמות, נקודות בדיקה, התאמה לקהל, לסאונד, ללוח הזמנים ולסגנון האירוע.`,
    };
  }

  return {
    title: `${i18n.PLANNING_CITY_VENUE_GUIDE.groupTitle} - ${regionLabel}`,
    subtitle: i18n.PLANNING_CITY_VENUE_GUIDE.groupSubtitle,
  };
};

export function getPlanningGuideGroups(i18n: I18nConstants) {
  const serviceGroups = [
    SEO_SERVICE_WEDDING,
    SEO_SERVICE_HENNA,
    SEO_SERVICE_BAR_BAT,
    SEO_SERVICE_EVENT,
  ].map(service => {
    const section = buildPlanningSectionForService(service, i18n);

    return {
      service,
      title: section.title,
      subtitle: section.subtitle,
      items: section.items,
    };
  });
  const cityVenueGuideItems = getCityVenueGuideArticles(i18n).map(article =>
    planningItemFromArticle(article),
  );

  if (!cityVenueGuideItems.length) return serviceGroups;

  const cityVenueGuideGroups = CITY_VENUE_GUIDE_REGION_ORDER.map(region => {
    const items = cityVenueGuideItems.filter(
      item =>
        item.locationCity && findRegionByCity(item.locationCity) === region,
    );

    if (!items.length) return null;

    const copy = buildCityVenueRegionCopy(region, i18n);

    return {
      service: `city_venue_guides_${region}`,
      region,
      title: copy.title,
      subtitle: copy.subtitle,
      items,
    };
  }).filter((group): group is NonNullable<typeof group> => Boolean(group));

  if (!cityVenueGuideGroups.length) return serviceGroups;

  return [...serviceGroups, ...cityVenueGuideGroups];
}
